import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useApp } from "../context/AppContext";
import { computeSemNgrams } from "../utils/semNgrams";

export default function SeoEditorView() {
  const { proj, activeId, getStore, setStore, dataRef, projects, setProjects, apiFetch,
    saveClusters, saveContent, saveStructure, saveSemClusters, saveLinks,
    flash, setView, view, dark, setDark, t, card, inp, btnP, btnG, btnSm, stitle, muted, th, td,
    userRole, currentUser, loadProjectData, openP, infoId, setInfoId, setActiveId } = useApp();
  if (!proj) return null;
  const store = getStore(activeId);
  const clusters = store.clusters || [];
  const [customUrls, setCustomUrls] = useState(() => { try { return JSON.parse(localStorage.getItem('cu_editor_' + activeId) || '[]'); } catch { return []; } });
  const [addUrlInput, setAddUrlInput] = useState('');
  const saveCustomUrls = (arr) => { setCustomUrls(arr); try { localStorage.setItem('cu_editor_' + activeId, JSON.stringify(arr)); } catch {} };
  const handleAddCustomUrl = () => {
    const u = addUrlInput.trim().replace(/\/+$/, '');
    if (!u || !/^https?:\/\//.test(u)) { flash('Введіть повний URL (https://...)'); return; }
    if (customUrls.includes(u)) { setSelectedUrl(u); setAddUrlInput(''); return; }
    saveCustomUrls([u, ...customUrls]);
    setAddUrlInput('');
    setSelectedUrl(u);
  };
  const removeCustomUrl = (u) => { saveCustomUrls(customUrls.filter(cu => cu !== u)); if (selectedUrl === u) setSelectedUrl(''); };
  const allUrls = [...new Set([...customUrls.filter(u => !clusters.some(c => c.url === u)), ...clusters.map(c => c.url)])];

  const semNgrams = useMemo(() => computeSemNgrams(clusters), [clusters]);

  const [selectedUrl, setSelectedUrl] = useState("");
  const [savedTexts, setSavedTexts] = useState({});
  const [pageStats, setPageStatsRaw] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`seo_stats_${activeId}`) || "{}"); } catch { return {}; }
  });
  const setPageStats = (updater) => {
    setPageStatsRaw(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      try { localStorage.setItem(`seo_stats_${activeId}`, JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const [syncingAll, setSyncingAll] = useState(false);
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("seo_editor_sort_" + activeId) || "default");
  useEffect(() => { try { localStorage.setItem("seo_editor_sort_" + activeId, sortBy); } catch {} }, [sortBy, activeId]);
  // Фільтр за статусом (all|draft|pending_client|approved|rejected|published|unpublished|none)
  const [statusFilter, setStatusFilter] = useState(() => localStorage.getItem("seo_editor_statusfilter_" + activeId) || "all");
  useEffect(() => { try { localStorage.setItem("seo_editor_statusfilter_" + activeId, statusFilter); } catch {} }, [statusFilter, activeId]);
  // publishedUrls / statusByUrl оголошені тут (вище urls-useMemo), щоб фільтр міг ними користуватись.
  const [publishedUrls, setPublishedUrls] = useState(new Set());
  const [statusByUrl, setStatusByUrl] = useState({}); // url → status ('draft'|'pending_client'|'approved'|'rejected')
  const urls = useMemo(() => {
    let list;
    if (sortBy === "withText") list = [...allUrls].sort((a, b) => (savedTexts[b]?.plain_text ? 1 : 0) - (savedTexts[a]?.plain_text ? 1 : 0));
    else if (sortBy === "withoutText") list = [...allUrls].sort((a, b) => (savedTexts[a]?.plain_text ? 1 : 0) - (savedTexts[b]?.plain_text ? 1 : 0));
    else list = allUrls;
    if (statusFilter && statusFilter !== "all") {
      list = list.filter(u => {
        if (statusFilter === "published") return publishedUrls.has(u);
        if (statusFilter === "unpublished") return !publishedUrls.has(u);
        if (statusFilter === "none") return !statusByUrl[u];
        return statusByUrl[u] === statusFilter;
      });
    }
    return list;
  }, [allUrls, savedTexts, sortBy, statusFilter, publishedUrls, statusByUrl]);
  const [validatingLinks, setValidatingLinks] = useState(false);
  const [stripping, setStripping] = useState(false);
  const [linkReport, setLinkReport] = useState(null);
  const [republishing, setRepublishing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seoScore, setSeoScore] = useState(null);
  const seoScoreRef = useRef(null);
  useEffect(() => { seoScoreRef.current = seoScore; }, [seoScore]);
  const [aiFixIter, setAiFixIter] = useState(null); // { current, max, score }
  const [uniquenessResult, setUniquenessResult] = useState(null);
  const [checkingInternal, setCheckingInternal] = useState(false);
  const [checkingExternal, setCheckingExternal] = useState(false);
  const [expandedChecks, setExpandedChecks] = useState({});
  const [bulkOverwrite, setBulkOverwrite] = useState(false);
  const [bulkTask, setBulkTask] = useState(null);
  const [bulkStarting, setBulkStarting] = useState(false);
  // Модалка «Побажання до генерації» + текст вимог (зберігається per-project).
  const [showGenModal, setShowGenModal] = useState(false);
  const [genRequirements, setGenRequirements] = useState("");
  useEffect(() => { if (!activeId) return; try { setGenRequirements(localStorage.getItem("seo_gen_req_" + activeId) || ""); } catch { /* ignore */ } }, [activeId]);
  useEffect(() => { if (!activeId) return; try { localStorage.setItem("seo_gen_req_" + activeId, genRequirements); } catch { /* ignore */ } }, [genRequirements, activeId]);
  const [genLinkCount, setGenLinkCount] = useState(3); // к-сть внутрішніх посилань у згенерованому тексті (0 = без)
  useEffect(() => { if (!activeId) return; try { const v = localStorage.getItem("seo_gen_links_" + activeId); if (v !== null) setGenLinkCount(Number(v)); } catch { /* ignore */ } }, [activeId]);
  useEffect(() => { if (!activeId) return; try { localStorage.setItem("seo_gen_links_" + activeId, String(genLinkCount)); } catch { /* ignore */ } }, [genLinkCount, activeId]);
  const [pulling, setPulling] = useState(false);
  const [pullResult, setPullResult] = useState(null); // {running,total,done,pulled,pulledRaw,emptySlots,noMarkup,errors,items:[{url,status}]}
  const [pullSelector, setPullSelector] = useState(""); // кастомний CSS-селектор блоку тексту (per-project)
  useEffect(() => {
    if (!activeId) return;
    try { setPullSelector(localStorage.getItem("pullSelector_" + activeId) || ""); } catch { /* ignore */ }
  }, [activeId]);
  const savePullSelector = (v) => { setPullSelector(v); try { localStorage.setItem("pullSelector_" + activeId, v); } catch { /* ignore */ } };
  // Середня позиція сторінки (з /positions/avg-by-page) для окремого стовпця.
  const [avgByUrl, setAvgByUrl] = useState({}); // normUrl -> avgPos
  const [posThreshold, setPosThreshold] = useState(10);
  useEffect(() => {
    if (!activeId) return;
    const norm = (u) => String(u || "").toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").split("?")[0].split("#")[0].replace(/\/+$/, "");
    apiFetch(`/api/projects/${activeId}/positions/avg-by-page`).then(r => r.json()).then(d => {
      if (d && Array.isArray(d.pages)) { const m = {}; d.pages.forEach(p => { if (p.avgPos != null) m[norm(p.url)] = p.avgPos; }); setAvgByUrl(m); setPosThreshold(d.threshold || 10); }
    }).catch(() => {});
  }, [activeId]);
  const avgPosFor = (url) => avgByUrl[String(url || "").toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").split("?")[0].split("#")[0].replace(/\/+$/, "")] ?? null;
  const [selectedUrls, setSelectedUrls] = useState(() => new Set());
  const toggleUrlSelect = (url) => setSelectedUrls(prev => { const n = new Set(prev); if (n.has(url)) n.delete(url); else n.add(url); return n; });
  const toggleSelectAll = () => setSelectedUrls(prev => prev.size === urls.length ? new Set() : new Set(urls));
  // ── Передача копірайтеру ──
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignDeadline, setAssignDeadline] = useState("");
  const [assignNote, setAssignNote] = useState("");
  const [assigning, setAssigning] = useState(false);
  const openAssignModal = () => {
    if (selectedUrls.size === 0) return;
    setShowAssignModal(true);
  };
  // Keywords for an arbitrary URL (same logic as pageKeywords, but per-url for the brief snapshot)
  const keywordsForUrl = (url) => {
    const kws = []; const seen = new Set();
    const uN = normU(url);
    for (const cl of clusters) {
      if (normU(cl.url) !== uN) continue;
      for (const k of (cl.keywords || [])) {
        const kw = typeof k === "string" ? { keyword: k, frequency: 1 } : { keyword: k.keyword || k.name || "", frequency: k.frequency || k.freq || 1 };
        if (kw.keyword && !seen.has(kw.keyword.toLowerCase())) { seen.add(kw.keyword.toLowerCase()); kws.push(kw); }
      }
    }
    return kws.sort((a, b) => b.frequency - a.frequency);
  };
  const submitAssign = async () => {
    setAssigning(true);
    const targets = Array.from(selectedUrls);
    let ok = 0, fail = 0;
    for (const url of targets) {
      const kws = keywordsForUrl(url);
      // n-grams for this url (same shape pageNgrams uses)
      let ngrams = [];
      let pageData = semNgrams && semNgrams.byPage && semNgrams.byPage.get(url);
      if (!pageData && semNgrams && semNgrams.byPage) { // слеш-толерантний fallback
        const uN = normU(url);
        for (const [k, v] of semNgrams.byPage) { if (normU(k) === uN) { pageData = v; break; } }
      }
      if (pageData) {
        ngrams = [...(pageData.unigrams || []), ...(pageData.bigrams || []), ...(pageData.trigrams || [])]
          .filter(g => g.priority === "high" || g.priority === "medium")
          .sort((a, b) => b.weight - a.weight).slice(0, 30)
          .map(g => ({ gram: g.gram }));
      }
      // per-url recommended volume (mirrors recommendedLength logic)
      const kc = kws.length;
      const recLen = kc <= 5 ? { min: 1500, max: 2500 } : kc <= 15 ? { min: 2500, max: 4000 } : kc <= 30 ? { min: 4000, max: 6000 } : { min: 5000, max: 8000 };
      const saved = savedTexts[url];
      const brief = {
        keywords: kws,
        ngrams,
        recommendedLength: recLen,
        liveUrl: url,
        title: saved?.title || "",
        h1: saved?.h1 || "",
        description: saved?.description || "",
        note: assignNote || "",
      };
      try {
        const r = await apiFetch(`/api/copywriter/assign`, { method: "POST", body: JSON.stringify({ project_id: activeId, url, deadline: assignDeadline || null, brief }) });
        if (r.ok) ok++; else fail++;
      } catch { fail++; }
    }
    setAssigning(false);
    setShowAssignModal(false);
    setSelectedUrls(new Set());
    flash(`Передано копірайтерам: ${ok}${fail ? `, помилок ${fail}` : ""} ✓`);
  };
  const editorRef = useRef(null);
  const debounceRef = useRef(null);
  const lastUrlRef = useRef("");

  // Get keywords for selected URL
  // URL-нормалізація для матчингу семантики: ігноруємо трейлінг-слеш, протокол,
  // www, query/hash. Інакше дублі URL (напр. .../page vs .../page/) не бачать ключів.
  const normU = (u) => String(u || "").toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").split("?")[0].split("#")[0].replace(/\/+$/, "");

  const pageKeywords = useMemo(() => {
    if (!selectedUrl) return [];
    const selN = normU(selectedUrl);
    const kws = [];
    const seen = new Set();
    for (const c of clusters) {
      if (normU(c.url) !== selN) continue;
      for (const k of (c.keywords || [])) {
        const kw = typeof k === "string" ? { keyword: k, frequency: 1 } : { keyword: k.keyword || k.name || "", frequency: k.frequency || k.freq || 1 };
        if (kw.keyword && !seen.has(kw.keyword.toLowerCase())) {
          seen.add(kw.keyword.toLowerCase());
          kws.push(kw);
        }
      }
    }
    return kws.sort((a, b) => b.frequency - a.frequency);
  }, [selectedUrl, clusters]);

  // Статистика ключів по КОЖНІЙ сторінці (к-сть унікальних + сумарна частотність) — для таблиці списку
  const kwStatsByUrl = useMemo(() => {
    const m = new Map();
    for (const c of clusters) {
      const key = normU(c.url);
      let e = m.get(key); if (!e) { e = { count: 0, freqSum: 0, seen: new Set() }; m.set(key, e); }
      for (const k of (c.keywords || [])) {
        const kw = typeof k === "string" ? { keyword: k, frequency: 1 } : { keyword: k.keyword || k.name || "", frequency: k.frequency || k.freq || 1 };
        const lk = (kw.keyword || "").toLowerCase(); if (!lk || e.seen.has(lk)) continue;
        e.seen.add(lk); e.count++; e.freqSum += (Number(kw.frequency) || 0);
      }
    }
    return m;
  }, [clusters]);
  const kwStatFor = (url) => kwStatsByUrl.get(normU(url)) || { count: 0, freqSum: 0 };
  const extractH1 = (html) => { const mm = String(html || "").match(/<h1[^>]*>([\s\S]*?)<\/h1>/i); return mm ? mm[1].replace(/<[^>]+>/g, "").trim() : ""; };

  const hfKeyword = pageKeywords[0]?.keyword || "";
  const hfKeys = pageKeywords.filter(k => k.frequency > 1000);
  const mfKeys = pageKeywords.filter(k => k.frequency >= 100 && k.frequency <= 1000);
  const lfKeys = pageKeywords.filter(k => k.frequency >= 10 && k.frequency < 100);
  const microKeys = pageKeywords.filter(k => k.frequency >= 1 && k.frequency < 10);

  // Page ngrams from semNgrams
  const pageNgrams = useMemo(() => {
    if (!semNgrams || !selectedUrl) return [];
    let pageData = semNgrams.byPage.get(selectedUrl);
    if (!pageData) { // fallback: слеш-толерантний пошук (шлях зберігаємо → RU/UA не змішуються)
      const selN = normU(selectedUrl);
      for (const [k, v] of semNgrams.byPage) { if (normU(k) === selN) { pageData = v; break; } }
    }
    if (!pageData) return [];
    const all = [...(pageData.unigrams || []), ...(pageData.bigrams || []), ...(pageData.trigrams || [])];
    return all.filter(g => g.priority === "high" || g.priority === "medium").sort((a, b) => b.weight - a.weight).slice(0, 30);
  }, [semNgrams, selectedUrl]);

  // Recommended text length
  const recommendedLength = useMemo(() => {
    const kwCount = pageKeywords.length;
    if (kwCount <= 5) return { min: 1500, max: 2500 };
    if (kwCount <= 15) return { min: 2500, max: 4000 };
    if (kwCount <= 30) return { min: 4000, max: 6000 };
    return { min: 5000, max: 8000 };
  }, [pageKeywords]);

  // Load saved texts on mount
  useEffect(() => {
    apiFetch(`/api/projects/${activeId}/seo-texts`)
      .then(r => r.json())
      .then(rows => {
        const map = {};
        for (const r of rows) map[r.url] = r;
        setSavedTexts(map);
      })
      .catch(() => {});
  }, [activeId]);

  // Bulk generation task: load latest on mount, poll while in_progress
  useEffect(() => {
    if (!activeId) return;
    let cancel = false;
    let timer = null;

    const refreshSavedTexts = () => {
      apiFetch(`/api/projects/${activeId}/seo-texts`).then(r => r.json()).then(rows => {
        if (cancel) return;
        const map = {};
        for (const r of rows) map[r.url] = r;
        setSavedTexts(map);
      }).catch(() => {});
    };

    const tick = async () => {
      try {
        const r = await apiFetch(`/api/projects/${activeId}/seo-texts/bulk-generate/status`);
        const t = await r.json();
        if (cancel) return;
        setBulkTask(t || null);
        if (t && t.status === "in_progress") {
          timer = setTimeout(tick, 2500);
        } else if (t && (t.status === "done" || t.status === "completed" || t.status === "success")) {
          // Final refresh of texts so the table reflects new content
          refreshSavedTexts();
        }
      } catch {
        if (!cancel) timer = setTimeout(tick, 5000);
      }
    };

    tick();
    return () => { cancel = true; if (timer) clearTimeout(timer); };
  }, [activeId]);

  // Активний поллінг прогресу, ПОКИ триває генерація. Окремий ефект — щоб
  // цикл (пере)запускався щоразу, коли статус стає in_progress (напр. після
  // натискання кнопки), а не тільки на mount. Без цього шкала «застигала» на 0%.
  useEffect(() => {
    if (!activeId || bulkTask?.status !== "in_progress") return;
    let cancel = false, timer = null;
    const poll = async () => {
      try {
        const r = await apiFetch(`/api/projects/${activeId}/seo-texts/bulk-generate/status`);
        const t = await r.json();
        if (cancel) return;
        setBulkTask(t || null);
        if (t && t.status === "in_progress") {
          timer = setTimeout(poll, 2000);
        } else {
          apiFetch(`/api/projects/${activeId}/seo-texts`).then(r => r.json()).then(rows => {
            if (cancel) return;
            const map = {}; for (const row of rows) map[row.url] = row; setSavedTexts(map);
          }).catch(() => {});
        }
      } catch { if (!cancel) timer = setTimeout(poll, 4000); }
    };
    timer = setTimeout(poll, 1200);
    return () => { cancel = true; if (timer) clearTimeout(timer); };
  }, [activeId, bulkTask?.status]);

  // Клік по кнопці → відкриваємо модалку з полем побажань (замість native confirm).
  const startBulkGenerate = () => {
    if (bulkStarting || bulkTask?.status === "in_progress") return;
    if (!urls.length) { flash("Немає сторінок"); return; }
    setShowGenModal(true);
  };

  // Реальний запуск (з модалки), з побажаннями клієнта у промпті.
  const runBulkGenerate = async () => {
    if (bulkStarting) return;
    const eligible = urls.length;
    if (!eligible) { flash("Немає сторінок"); setShowGenModal(false); return; }
    const explicit = selectedUrls.size > 0;
    setShowGenModal(false);
    setBulkStarting(true);
    try {
      const reqText = (genRequirements || "").trim();
      const body = explicit
        ? { urls: Array.from(selectedUrls) }
        : { overwrite: bulkOverwrite };
      if (reqText) body.requirements = reqText;
      body.linkCount = genLinkCount;
      const r = await apiFetch(`/api/projects/${activeId}/seo-texts/bulk-generate`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) {
        flash("Помилка: " + (d.error || r.status));
      } else if (d.skipped) {
        const msg = d.skipped === "all_already_filled" ? "Всі сторінки вже мають тексти — використайте чекбокс перезапису або позначте конкретні"
          : d.skipped === "selected_have_no_meta" ? "У вибраних сторінок немає мета-тегів — спершу згенеруйте Title/H1/Description"
          : "Немає кластерів з мета-тегами — спершу згенеруйте Title/H1/Description";
        flash(msg);
      } else {
        flash(`Запущено генерацію для ${d.total} сторінок`);
        setBulkTask({ status: "in_progress", progress: { done: 0, total: d.total, ok: 0, errors: 0 } });
        if (explicit) setSelectedUrls(new Set()); // clear selection after kicking off
      }
    } catch (e) { flash("Помилка: " + e.message); }
    setBulkStarting(false);
  };

  // Pull existing on-site text into the editor via [data-spalah] markup (server-side fetch).
  const exportDownload = async (url, fb) => {
    try {
      const r = await apiFetch(url);
      if (!r.ok) { let e = {}; try { e = await r.json(); } catch {} alert(e.error || "Помилка експорту"); return; }
      const blob = await r.blob();
      const cd = r.headers.get("Content-Disposition") || "";
      let name = fb; const m = cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="?([^";]+)"?/i);
      if (m) { try { name = decodeURIComponent(m[1]); } catch { name = m[1]; } }
      const a = document.createElement("a"); const u = URL.createObjectURL(blob); a.href = u; a.download = name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
    } catch (e) { alert("Помилка: " + e.message); }
  };
  const exportDocx = () => { if (!selectedUrl) { alert("Спершу відкрийте текст сторінки."); return; } exportDownload(`/api/projects/${activeId}/seo-texts/export?url=${encodeURIComponent(selectedUrl)}`, "text.docx"); };
  const exportAllZip = () => exportDownload(`/api/projects/${activeId}/seo-texts/export-all`, "texts.zip");

  const pullFromSite = async () => {
    if (pulling) return;
    const explicit = selectedUrls.size > 0;
    const targets = explicit ? Array.from(selectedUrls) : urls;
    if (!targets.length) { flash("Немає сторінок"); return; }
    if (!confirm(`Підтягнути текст із сайту для ${targets.length} ${explicit ? "вибраних " : ""}сторінок?\nСпершу блок data-spalah; якщо його немає — основний текст сторінки (для рерайту, без зворотної публікації через SDK).`)) return;

    setPulling(true);
    const acc = { running: true, total: targets.length, done: 0, pulled: 0, pulledRaw: 0, emptySlots: 0, noMarkup: 0, errors: 0, items: [] };
    setPullResult({ ...acc });
    const BATCH = 5;
    try {
      for (let i = 0; i < targets.length; i += BATCH) {
        const batch = targets.slice(i, i + BATCH);
        try {
          const r = await apiFetch(`/api/projects/${activeId}/seo-texts/pull-from-site`, {
            method: "POST", body: JSON.stringify({ urls: batch, selector: pullSelector || undefined }),
          });
          const d = await r.json();
          if (!r.ok) {
            batch.forEach(u => acc.items.push({ url: u, status: "error" }));
            acc.errors += batch.length;
          } else {
            (d.pulled || []).forEach(x => acc.items.push({ url: x.url, status: "pulled", count: x.count }));
            (d.pulledRaw || []).forEach(x => acc.items.push({ url: x.url, status: "raw", count: x.count }));
            (d.emptySlots || []).forEach(x => acc.items.push({ url: x.url, status: "empty" }));
            (d.noMarkup || []).forEach(u => acc.items.push({ url: u, status: "noMarkup" }));
            (d.errors || []).forEach(x => acc.items.push({ url: x.url || x, status: "error", error: x.error }));
            const s = d.summary || {};
            acc.pulled += s.pulled || 0; acc.pulledRaw += s.pulledRaw || 0;
            acc.emptySlots += s.emptySlots || 0; acc.noMarkup += s.noMarkup || 0; acc.errors += s.errors || 0;
          }
        } catch (e) {
          batch.forEach(u => acc.items.push({ url: u, status: "error", error: e.message }));
          acc.errors += batch.length;
        }
        acc.done = Math.min(i + BATCH, targets.length);
        setPullResult({ ...acc, items: [...acc.items] });
      }
      // reload saved texts into editor
      const rows = await apiFetch(`/api/projects/${activeId}/seo-texts`).then(x => x.json());
      const map = {}; for (const row of rows) map[row.url] = row;
      setSavedTexts(map);
      if (explicit) setSelectedUrls(new Set());
    } catch (e) { flash("Помилка: " + e.message); }
    acc.running = false;
    setPullResult({ ...acc, items: [...acc.items] });
    setPulling(false);
  };

  // When URL changes, set editor content from saved
  useEffect(() => {
    if (!editorRef.current || !selectedUrl) return;
    if (lastUrlRef.current === selectedUrl) return;
    lastUrlRef.current = selectedUrl;
    const saved = savedTexts[selectedUrl];
    editorRef.current.innerHTML = saved?.html_content || "";
    setUniquenessResult(null);
    runSeoAnalysis();
  }, [selectedUrl, savedTexts]);

  const getPlainText = () => {
    if (!editorRef.current) return "";
    return editorRef.current.innerText || "";
  };

  const getHtml = () => {
    if (!editorRef.current) return "";
    return editorRef.current.innerHTML || "";
  };

  // Чи вже використано ключ/n-граму в тексті (для зеленого підсвічування чипів)
  const ngramCovered = (gram) => {
    const plain = getPlainText().toLowerCase();
    if (!plain) return false;
    const p = String(gram || "").toLowerCase().trim();
    if (!p) return false;
    if (plain.includes(p)) return true; // точний збіг
    const words = plain.match(/[a-zа-яёіїєґ]+(?:['-][a-zа-яёіїєґ]+)*/gi) || [];
    const toks = (p.match(/[a-zа-яёіїєґ]+(?:['-][a-zа-яёіїєґ]+)*/gi) || []).filter(t => t.length >= 3);
    if (!toks.length) return false;
    // усі значущі токени присутні словоформами (startsWith в обидва боки)
    return toks.every(t => words.some(w => w === t || w.startsWith(t) || (t.length >= 5 && w.length >= 4 && t.startsWith(w.slice(0, t.length - 1)))));
  };

  // Auto-save with debounce
  const autoSaveRef = useRef(null);
  const autoSave = () => {
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => {
      if (!editorRef.current || !selectedUrl) return;
      const htmlContent = editorRef.current.innerHTML || "";
      const plainText = editorRef.current.innerText || "";
      if (!plainText.trim()) return;
      apiFetch(`/api/projects/${activeId}/seo-texts`, {
        method: "PUT",
        body: JSON.stringify({ url: selectedUrl, html_content: htmlContent, plain_text: plainText, seo_score: seoScore }),
      }).then(() => {
        setSavedTexts(prev => ({ ...prev, [selectedUrl]: { ...(prev[selectedUrl] || {}), html_content: htmlContent, plain_text: plainText, seo_score: seoScore } }));
      }).catch(() => {});
    }, 2000);
  };

  // SEO Analysis
  const runSeoAnalysis = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const text = getPlainText().toLowerCase();
      const html = getHtml();
      const charCount = text.replace(/\s/g, "").length;
      const wordCount = text.split(/\s+/).filter(Boolean).length;

      if (!text.trim()) {
        setSeoScore(null);
        return;
      }

      // ── Толерантний до словоформ матчинг ключів (укр/рос склонения) ──
      // "ремонт форсунок львів" зараховується у тексті "ремонт форсунок у Львові".
      const STOP_KW = new Set(["та","і","в","у","на","з","із","за","до","від","по","для","як","що","це","и","с","по","за","из","от","не","the","a","an","of","in","for","on","to","and","or"]);
      const textWords = Array.from(new Set(text.match(/[a-zа-яёіїєґ]+(?:['-][a-zа-яёіїєґ]+)*/gi) || []));
      const levLE = (a, b, max) => { // bounded Levenshtein, рання зупинка
        const m = a.length, n = b.length;
        if (Math.abs(m - n) > max) return false;
        let prev = Array.from({ length: n + 1 }, (_, i) => i);
        for (let i = 1; i <= m; i++) {
          const cur = [i]; let best = i;
          for (let j = 1; j <= n; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            const v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
            cur[j] = v; if (v < best) best = v;
          }
          if (best > max) return false;
          prev = cur;
        }
        return prev[n] <= max;
      };
      const tokMatch = (tok, w) => {
        if (w === tok) return true;
        if (w.length >= tok.length && w.startsWith(tok)) return true;      // ремонт→ремонту
        if (tok.length >= 5 && w.length >= tok.length - 1 && tok.startsWith(w.slice(0, tok.length - 1))) return true;
        return levLE(tok, w, tok.length <= 4 ? 1 : 2);                     // львів↔львові, тернопіль↔тернополі
      };
      const kwTokens = (phrase) => (String(phrase).toLowerCase().match(/[a-zа-яёіїєґ]+(?:['-][a-zа-яёіїєґ]+)*/gi) || []).filter(t => t.length >= 3 && !STOP_KW.has(t));
      const phraseCovered = (phrase) => {
        if (text.includes(String(phrase).toLowerCase())) return true;      // точний збіг — швидкий шлях
        const toks = kwTokens(phrase);
        if (!toks.length) return false;
        return toks.every(t => textWords.some(w => tokMatch(t, w)));
      };

      const checks = [];
      const hasKeywords = pageKeywords.length > 0;

      // ── SEO ПЕРЕВІРКИ (75 балів) ──
      // 1. HF keyword (15 pts)
      if (!hasKeywords) {
        checks.push({ name: "ВЧ ключ", weight: 15, status: "warn", detail: "Немає семантики — завантажте CSV або зберіть ключі" });
      } else if (hfKeyword) {
        const hfLower = hfKeyword.toLowerCase();
        const hfRegex = new RegExp(hfLower.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
        const hfMatches = (text.match(hfRegex) || []).length;
        const inFirst500 = text.substring(0, 500).includes(hfLower);
        const density = wordCount > 0 ? (hfMatches / wordCount) * 100 : 0;
        const ok = hfMatches >= 2 && inFirst500 && density < 3;
        const warn = hfMatches >= 1 && density < 3;
        checks.push({
          name: "ВЧ ключ", weight: 15, status: ok ? "ok" : warn ? "warn" : "fail",
          detail: `"${hfKeyword}": ${hfMatches}x, ${inFirst500 ? "в первых 500" : "не в первых 500"}, плотность ${density.toFixed(1)}%`,
        });
      }

      // 2. MF coverage (15 pts)
      if (!hasKeywords) {
        checks.push({ name: "СЧ покриття", weight: 15, status: "warn", detail: "Немає семантики" });
      } else if (mfKeys.length) {
        const covered = mfKeys.filter(k => phraseCovered(k.keyword));
        const pct = covered.length / mfKeys.length;
        const ok = pct >= 0.5;
        const warn = pct >= 0.3;
        checks.push({
          name: "СЧ покриття", weight: 15, status: ok ? "ok" : warn ? "warn" : "fail",
          detail: `${covered.length}/${mfKeys.length} (${Math.round(pct * 100)}%)`,
        });
      } else {
        checks.push({ name: "СЧ покриття", weight: 15, status: "ok", detail: "Нет СЧ ключей" });
      }

      // 3. LF coverage (10 pts)
      if (!hasKeywords) {
        checks.push({ name: "НЧ покриття", weight: 10, status: "warn", detail: "Немає семантики" });
      } else if (lfKeys.length) {
        const covered = lfKeys.filter(k => phraseCovered(k.keyword));
        const pct = covered.length / lfKeys.length;
        const ok = pct >= 0.3;
        const warn = pct >= 0.15;
        checks.push({
          name: "НЧ покриття", weight: 10, status: ok ? "ok" : warn ? "warn" : "fail",
          detail: `${covered.length}/${lfKeys.length} (${Math.round(pct * 100)}%)`,
        });
      } else {
        checks.push({ name: "НЧ покриття", weight: 10, status: "ok", detail: "Нет НЧ ключей" });
      }

      // 4. N-grams (10 pts)
      if (!hasKeywords) {
        checks.push({ name: "N-грами", weight: 10, status: "warn", detail: "Немає семантики" });
      } else if (pageNgrams.length) {
        const covered = pageNgrams.filter(g => phraseCovered(g.gram));
        const pct = covered.length / pageNgrams.length;
        const ok = pct >= 0.6;
        const warn = pct >= 0.4;
        checks.push({
          name: "N-грами", weight: 10, status: ok ? "ok" : warn ? "warn" : "fail",
          detail: `${covered.length}/${pageNgrams.length} (${Math.round(pct * 100)}%)`,
        });
      } else {
        checks.push({ name: "N-грами", weight: 10, status: "ok", detail: "Нет N-грам" });
      }

      // 5. Volume (10 pts)
      const volOk = charCount >= recommendedLength.min && charCount <= recommendedLength.max;
      const volWarn = charCount >= recommendedLength.min * 0.7;
      checks.push({
        name: "Обсяг", weight: 10, status: volOk ? "ok" : volWarn ? "warn" : "fail",
        detail: `${charCount} символів (рек. ${recommendedLength.min}-${recommendedLength.max})`,
      });

      // 6. Structure (10 pts)
      const hasH2 = /<h2/i.test(html);
      const hasH3 = /<h3/i.test(html);
      const hasParagraphs = (html.match(/<p/gi) || []).length >= 2;
      const structOk = hasH2 && hasParagraphs;
      const structWarn = hasH2 || hasH3;
      checks.push({
        name: "Структура", weight: 10, status: structOk ? "ok" : structWarn ? "warn" : "fail",
        detail: `H2: ${hasH2 ? "+" : "-"}, H3: ${hasH3 ? "+" : "-"}, Абзаци: ${hasParagraphs ? "+" : "-"}`,
      });

      // 7. Spam check (5 pts)
      const spamKeys = pageKeywords.filter(k => {
        const cnt = (text.match(new RegExp(k.keyword.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi")) || []).length;
        return wordCount > 0 && (cnt / wordCount) * 100 > 3;
      });
      checks.push({
        name: "Переспам", weight: 5, status: spamKeys.length === 0 ? "ok" : "fail",
        detail: spamKeys.length === 0 ? "Ок" : `Переспам: ${spamKeys.map(k => k.keyword).join(", ")}`,
      });

      // ── AEO ПЕРЕВІРКИ (AI Engine Optimization) ──
      const words = text.split(/\s+/).filter(Boolean);

      // 8. Answer Capsule — перші 200 слів містять ВЧ ключ (4 pts)
      const first200 = words.slice(0, 200).join(" ").toLowerCase();
      const hasKeyInFirst200 = hfKeyword ? first200.includes(hfKeyword.toLowerCase()) : true;
      checks.push({
        name: "AEO: Answer Capsule", weight: 4, status: hasKeyInFirst200 ? "ok" : "fail",
        detail: hasKeyInFirst200 ? `"${hfKeyword}" є в перших 200 словах — AI зможе процитувати` : `"${hfKeyword}" відсутній в перших 200 словах — AI пропустить`,
      });

      // 9. Перший абзац = пряма відповідь, не "вступ" (2 pts)
      const firstParagraph = text.split(/\n\n|\.\s/).filter(s => s.trim().length > 20)[0] || "";
      const startsWithFiller = /^(у цій статті|в даній|сьогодні ми|ми розповімо|давайте|у цьому|в этой статье|сегодня мы|давайте)/i.test(firstParagraph.trim());
      checks.push({
        name: "AEO: Прямий початок", weight: 3, status: startsWithFiller ? "fail" : "ok",
        detail: startsWithFiller ? "Текст починається з води — AI пропустить. Починайте з відповіді/факту" : "Початок без води — AI витягне як відповідь",
      });

      // 10. Довжина речень — середня <17 слів (3 pts)
      const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 10);
      const avgSentLen = sentences.length ? sentences.reduce((s, sent) => s + sent.trim().split(/\s+/).length, 0) / sentences.length : 0;
      const longSentences = sentences.filter(s => s.trim().split(/\s+/).length > 25).length;
      const sentOk = avgSentLen > 0 && avgSentLen <= 17;
      const sentWarn = avgSentLen <= 22;
      checks.push({
        name: "AEO: Довжина речень", weight: 4, status: sentOk ? "ok" : sentWarn ? "warn" : "fail",
        detail: `Середня: ${avgSentLen.toFixed(1)} сл/реч (ціль: <17). Довгих (>25): ${longSentences}`,
      });

      // 11. Щільність фактів — числа, %, дати (3 pts)
      const factMatches = text.match(/\d[\d\s,.]*(%|грн|₴|USD|\$|€|шт|л|кг|м²|мм|см|років|днів|годин|хвилин|разів|людей|клієнтів|проектів)?/g) || [];
      const factDensity = wordCount > 0 ? wordCount / Math.max(factMatches.length, 1) : 999;
      const factOk = factDensity <= 80;
      const factWarn = factDensity <= 150;
      checks.push({
        name: "AEO: Щільність фактів", weight: 4, status: factOk ? "ok" : factWarn ? "warn" : "fail",
        detail: `${factMatches.length} фактів на ${wordCount} слів (1:${Math.round(factDensity)}). Ціль: 1 на 80 слів. ${factOk ? "AI цитує в 4.2x частіше" : "Додайте числа, відсотки, дати"}`,
      });

      // 12. Без FAQ-блоку (2 pts) — це повноцінний опис послуги/товару, а не Q&A
      const hasFAQ = /часті\s*запитання|часто\s+задавані|часто\s+задаваемые|\bfaq\b|q\s*&\s*a|питання\s+та\s+відповіді|вопросы\s+и\s+ответы/i.test(text);
      checks.push({
        name: "Без FAQ-блоку", weight: 2, status: hasFAQ ? "fail" : "ok",
        detail: hasFAQ ? "Знайдено FAQ-блок — приберіть, текст має бути повноцінним описом послуги/товару" : "FAQ відсутній — це повноцінний опис",
      });

      // 13. Списки та таблиці (2 pts)
      const hasLists = /<[uo]l[\s>]/i.test(html);
      const hasTables = /<table[\s>]/i.test(html);
      const listTableOk = hasLists || hasTables;
      checks.push({
        name: "AEO: Списки/таблиці", weight: 4, status: listTableOk ? "ok" : "warn",
        detail: `Списки: ${hasLists ? "✓" : "✗"}, Таблиці: ${hasTables ? "✓" : "✗"}. ${listTableOk ? "AI легше витягує структуровані дані" : "Додайте списки або таблиці — 4.1x більше цитувань"}`,
      });

      // 14. Внутрішні посилання в тексті (3 pts)
      const internalLinkCount = (html.match(/<a[^>]*href=["'][^"']*["']/gi) || []).length;
      checks.push({
        name: "AEO: Внутрішні лінки", weight: 3, status: internalLinkCount >= 3 ? "ok" : internalLinkCount >= 1 ? "warn" : "fail",
        detail: `${internalLinkCount} посилань. ${internalLinkCount >= 3 ? "Добре для контексту AI" : "Додайте 3+ внутрішніх посилань"}`,
      });

      // ── E-E-A-T / TRUST (ТЗ «Контент-завод») ──
      // 15. E-E-A-T: Досвід та експертність (6 pts) — сигнали реального досвіду + автор
      const expMarkers = (text.match(/на (власному |нашому )?досвід|на практиц|ми (протестували|перевірили|рекомендуємо|використовуємо|лікуємо|надаємо|пропонуємо)|наш[іиа]? ?(фахівц|лікар|спеціаліст|експерт|команда|клінік)|перевірено (лікар|експерт)|\d+\s*(рок|літ|год)[а-яі]*\s*досвід|наприклад|наш кейс|власний досвід/gi) || []);
      const hasAuthor = /author|rel=["']author|datepublished/i.test(html) || /автор:|підготував|перевірено лікар|перевірив лікар/i.test(text);
      const expOk = expMarkers.length >= 2 || (expMarkers.length >= 1 && hasAuthor);
      const expWarn = expMarkers.length >= 1 || hasAuthor;
      checks.push({
        name: "E-E-A-T: Досвід", weight: 6, status: expOk ? "ok" : expWarn ? "warn" : "fail",
        detail: `Маркерів досвіду: ${expMarkers.length}${hasAuthor ? ", автор є" : ", автора нема"}. ${expOk ? "Google бачить реальний досвід (Experience)" : "Додайте «на практиці», приклади/кейс, автора-експерта"}`,
      });

      // 16. E-E-A-T: Джерела довіри (4 pts) — зовнішні посилання на авторитет або цитати
      const selfHost = (selectedUrl || "").replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0].toLowerCase();
      const extLinks = (html.match(/<a[^>]*href=["']https?:\/\/[^"']+["']/gi) || []).filter(a => { const m = a.match(/https?:\/\/([^/"']+)/i); const h = m ? m[1].replace(/^www\./, "").toLowerCase() : ""; return h && h !== selfHost; });
      const citeMarkers = /джерел|дослідженн|за даними|згідно (з|із)|за результатами|дослідження показ|за статистик/i.test(text);
      const trustOk = extLinks.length >= 1 || citeMarkers;
      checks.push({
        name: "E-E-A-T: Джерела", weight: 4, status: trustOk ? "ok" : "warn",
        detail: trustOk ? `Є посилання/цитати на авторитет (зовн. лінків: ${extLinks.length})` : "Додайте 1-2 посилання на джерело (дослідження/офіц. сайт) — Trust = фундамент E-E-A-T",
      });

      // 17. AEO: Актуальність / дата (3 pts) — свіжість важлива для Perplexity/AI
      const hasDate = /\b20(2[0-9]|3[0-9])\b|оновлено|станом на|актуально на/i.test(text) || /datetime=|datepublished|datemodified/i.test(html);
      checks.push({
        name: "AEO: Актуальність", weight: 3, status: hasDate ? "ok" : "warn",
        detail: hasDate ? "Є дата/рік — AI (особливо Perplexity) любить свіжість" : "Додайте рік/дату або «оновлено» — підвищує цитування в AI",
      });

      const totalScore = checks.reduce((sum, c) => sum + (c.status === "ok" ? c.weight : c.status === "warn" ? c.weight * 0.5 : 0), 0);
      setSeoScore({ total: Math.round(totalScore), checks });
    }, 500);
  };

  // Toolbar commands
  const execCmd = (tag) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    if (tag === "H2" || tag === "H3" || tag === "P") {
      document.execCommand("formatBlock", false, tag);
    } else if (tag === "B") {
      document.execCommand("bold");
    } else if (tag === "I") {
      document.execCommand("italic");
    } else if (tag === "UL") {
      document.execCommand("insertUnorderedList");
    } else if (tag === "OL") {
      document.execCommand("insertOrderedList");
    }
    runSeoAnalysis();
  };

  // Розмір шрифту виділеного тексту — inline <span style="font-size"> (переживає збереження та публікацію через SDK)
  const applyFontSize = (px) => {
    if (!editorRef.current || !px) return;
    editorRef.current.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    try { document.execCommand("styleWithCSS", false, false); } catch (e) {}
    document.execCommand("fontSize", false, "7"); // маркер → конвертуємо у span з px
    editorRef.current.querySelectorAll('font[size="7"]').forEach((el) => {
      const span = document.createElement("span");
      span.style.fontSize = px + "px";
      while (el.firstChild) span.appendChild(el.firstChild);
      el.replaceWith(span);
    });
    runSeoAnalysis();
    autoSave();
  };

  // Міжрядковий інтервал — inline line-height на блоках (абзаци/заголовки) виділення. Переживає збереження й публікацію.
  const applyLineHeight = (lh) => {
    if (!editorRef.current || !lh) return;
    editorRef.current.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const root = editorRef.current;
    const isBlock = (el) => el && el.nodeType === 1 && /^(P|H1|H2|H3|H4|H5|H6|LI|DIV|BLOCKQUOTE)$/.test(el.tagName);
    const blockOf = (node) => {
      let el = node && node.nodeType === 3 ? node.parentNode : node;
      while (el && el !== root && !isBlock(el)) el = el.parentNode;
      return el && el !== root ? el : null;
    };
    const blocks = new Set();
    const bs = blockOf(range.startContainer); if (bs) blocks.add(bs);
    const be = blockOf(range.endContainer); if (be) blocks.add(be);
    if (!range.collapsed) {
      const w = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
        acceptNode: (el) => isBlock(el) && range.intersectsNode(el) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP,
      });
      let n; while ((n = w.nextNode())) blocks.add(n);
    }
    if (blocks.size) blocks.forEach((b) => { b.style.lineHeight = String(lh); });
    else root.style.lineHeight = String(lh);
    runSeoAnalysis();
    autoSave();
  };

  // Блоки (абзаци/заголовки), що перетинають виділення
  const blocksInSelection = () => {
    const root = editorRef.current;
    const sel = window.getSelection();
    if (!root || !sel || sel.rangeCount === 0) return [];
    const range = sel.getRangeAt(0);
    const isBlock = (el) => el && el.nodeType === 1 && /^(P|H1|H2|H3|H4|H5|H6|LI|DIV|BLOCKQUOTE)$/.test(el.tagName);
    const blockOf = (node) => {
      let el = node && node.nodeType === 3 ? node.parentNode : node;
      while (el && el !== root && !isBlock(el)) el = el.parentNode;
      return el && el !== root ? el : null;
    };
    const set = new Set();
    const bs = blockOf(range.startContainer); if (bs) set.add(bs);
    const be = blockOf(range.endContainer); if (be) set.add(be);
    if (!range.collapsed) {
      const w = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
        acceptNode: (el) => isBlock(el) && range.intersectsNode(el) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP,
      });
      let n; while ((n = w.nextNode())) set.add(n);
    }
    return [...set];
  };

  // Відступ між абзацами (margin-bottom на блоках виділення)
  const applyBlockSpacing = (px) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    const blocks = blocksInSelection();
    (blocks.length ? blocks : [editorRef.current]).forEach((b) => { b.style.marginBottom = px + "px"; });
    runSeoAnalysis();
    autoSave();
  };

  // Скинути ручне форматування (розмір/інтервал/відступи) у виділенні
  const clearTextFormat = () => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const root = editorRef.current;
    const inSel = (el) => { try { return range.intersectsNode(el); } catch (e) { return false; } };
    [...root.querySelectorAll("[style]")].filter(inSel).forEach((el) => {
      el.style.lineHeight = ""; el.style.marginTop = ""; el.style.marginBottom = "";
      if (!el.style.length) el.removeAttribute("style");
    });
    [...root.querySelectorAll("span[style]")].filter(inSel).forEach((el) => {
      el.style.fontSize = "";
      if (!el.style.length) { while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el); el.remove(); }
    });
    runSeoAnalysis();
    autoSave();
  };

  // Paste sanitization
  const handlePaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    if (html) {
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      // Strip all except allowed tags
      const allowed = new Set(["H2", "H3", "P", "B", "I", "STRONG", "EM", "UL", "OL", "LI", "BR"]);
      const clean = (node) => {
        const children = [...node.childNodes];
        for (const child of children) {
          if (child.nodeType === 3) continue; // text
          if (child.nodeType === 1) {
            if (!allowed.has(child.tagName)) {
              // Replace with its children
              while (child.firstChild) child.parentNode.insertBefore(child.firstChild, child);
              child.parentNode.removeChild(child);
            } else {
              // Remove all attributes
              while (child.attributes.length) child.removeAttribute(child.attributes[0].name);
              clean(child);
            }
          }
        }
      };
      clean(tmp);
      document.execCommand("insertHTML", false, tmp.innerHTML);
    } else {
      document.execCommand("insertText", false, text);
    }
    runSeoAnalysis();
    autoSave();
  };

  // Save
  const handleSave = async () => {
    setSaving(true);
    try {
      const htmlContent = getHtml();
      const plainText = getPlainText();
      await apiFetch(`/api/projects/${activeId}/seo-texts`, {
        method: "PUT",
        body: JSON.stringify({ url: selectedUrl, html_content: htmlContent, plain_text: plainText, seo_score: seoScore }),
      });
      setSavedTexts(prev => ({ ...prev, [selectedUrl]: { ...(prev[selectedUrl] || {}), html_content: htmlContent, plain_text: plainText, seo_score: seoScore } }));
      flash("Текст збережено");
    } catch (e) {
      flash("Помилка збереження");
    }
    setSaving(false);
  };

  // Publish to site via SDK
  const [aiFixing, setAiFixing] = useState(false);
  const [showHtmlImport, setShowHtmlImport] = useState(false);
  const [showHtmlExport, setShowHtmlExport] = useState(false);
  const [htmlImportText, setHtmlImportText] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [readMoreUrls, setReadMoreUrls] = useState(new Set());
  const [readMore, setReadMore] = useState(false);
  const [clientFeedback, setClientFeedback] = useState({}); // url → { comment, reviewed_at }
  const [bulkStatusApplying, setBulkStatusApplying] = useState(false);
  const [factoryJobs, setFactoryJobs] = useState({});
  const factoryTimer = useRef(null);
  const FACTORY_STAGE = { queued: "🏭 у черзі", research: "🔬 рісерч", evidence: "📚 факт-пак", copywriter: "🖊 пише", humanness: "🧬 людяність", structure: "🧩 структура", tz_check: "📋 ТЗ", seo: "🔍 SEO", factcheck: "✅ факти", editor: "✨ редактор", arbiter: "🏁 арбітр", gemini_final: "💎 Gemini", specialist: "🧠 спеціаліст", done: "✅ готово", needs_human: "⚠️ руки", interrupted: "⚠️ перервано", error: "✖ помилка" };
  const pollFactory = async () => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/content-factory/status`);
      const d = await r.json().catch(() => ({}));
      const map = {}; (d.jobs || []).forEach(j => { map[j.url] = j; });
      setFactoryJobs(map);
      if ((d.jobs || []).some(j => j.status === "done")) {
        apiFetch(`/api/projects/${activeId}/seo-texts`).then(x => x.json()).then(rows => { const m = {}; for (const row of rows) m[row.url] = row; setSavedTexts(m); }).catch(() => {});
      }
      const active = (d.jobs || []).some(j => j.status === "queued" || j.status === "running");
      if (factoryTimer.current) clearTimeout(factoryTimer.current);
      if (active) factoryTimer.current = setTimeout(pollFactory, 4000);
    } catch {}
  };
  const sendToFactory = async () => {
    const list = Array.from(selectedUrls);
    if (!list.length) return;
    if (!window.confirm(`Передати ${list.length} сторінок у Контент-завод? Конвеєр ботів згенерує новий текст, статус оновлюватиметься автоматично.`)) return;
    try {
      const r = await apiFetch(`/api/projects/${activeId}/content-factory/run`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ urls: list }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error) { flash("Завод: " + (d.error || "помилка")); return; }
      flash(`🏭 У завод передано: ${d.queued}. Статус оновлюється автоматично.`);
      pollFactory();
    } catch (e) { flash("Помилка: " + e.message); }
  };
  useEffect(() => { if (activeId) pollFactory(); return () => { if (factoryTimer.current) clearTimeout(factoryTimer.current); }; }, [activeId]);
  // ── Human Editorial Gate [9] — редакторський чеклист для текстів із ризиком ──
  const [editorialUrl, setEditorialUrl] = useState(null);
  const [editorialData, setEditorialData] = useState(null);
  const openEditorial = async (url) => {
    setEditorialUrl(url); setEditorialData(null);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/content-factory/editorial?url=${encodeURIComponent(url)}`);
      setEditorialData(await r.json());
    } catch (e) { flash("Помилка: " + e.message); }
  };
  const toggleEditorialCheck = async (i) => {
    if (!editorialData) return;
    const ed = { ...(editorialData.job?.editorial || {}) }; ed["c" + i] = !ed["c" + i];
    setEditorialData({ ...editorialData, job: { ...editorialData.job, editorial: ed } });
    try { await apiFetch(`/api/projects/${activeId}/content-factory/editorial`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ url: editorialUrl, editorial: ed }) }); } catch {}
  };
  const [textComments, setTextComments] = useState([]); // thread for currently-selected text
  const [replyDraft, setReplyDraft] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);

  const currentTextId = savedTexts[selectedUrl]?.id;
  const loadComments = useCallback(() => {
    if (!activeId || !currentTextId) { setTextComments([]); return; }
    apiFetch(`/api/projects/${activeId}/seo-texts/${currentTextId}/comments`)
      .then(r => r.json()).then(d => setTextComments(Array.isArray(d) ? d : [])).catch(() => setTextComments([]));
  }, [activeId, currentTextId, apiFetch]);
  useEffect(() => { loadComments(); }, [loadComments]);

  const submitReply = async () => {
    const body = replyDraft.trim();
    if (!body || !currentTextId) return;
    setReplyBusy(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/seo-texts/${currentTextId}/comment`, {
        method: "POST", body: JSON.stringify({ body }),
      });
      const d = await r.json();
      if (!r.ok) flash("Помилка: " + (d.error || r.status));
      else { setReplyDraft(""); loadComments(); }
    } catch (e) { flash("Помилка: " + e.message); }
    setReplyBusy(false);
  };

  // Reload status maps from /seo-texts response
  const reloadStatusMaps = (rows) => {
    const published = new Set();
    const rm = new Set();
    const stat = {};
    const fb = {};
    (rows || []).forEach(r => {
      if (r.published) published.add(r.url);
      if (r.read_more) rm.add(r.url);
      if (r.status) stat[r.url] = r.status;
      if (r.client_comment || r.client_reviewed_at) fb[r.url] = { comment: r.client_comment, reviewed_at: r.client_reviewed_at };
    });
    setPublishedUrls(published);
    setReadMoreUrls(rm);
    setStatusByUrl(stat);
    setClientFeedback(fb);
  };

  // Load published+status on mount
  useEffect(() => {
    if (!activeId) return;
    apiFetch(`/api/projects/${activeId}/seo-texts`).then(r => r.json()).then(rows => reloadStatusMaps(rows)).catch(() => {});
  }, [activeId]);

  const applyBulkStatus = async (newStatus) => {
    if (selectedUrls.size === 0) return;
    const labels = { draft: "Чернетка", pending_client: "На погодженні у клієнта", approved: "Погоджено клієнтом", rejected: "Відхилено (переписати)" };
    if (!confirm(`Змінити статус ${selectedUrls.size} текстів на «${labels[newStatus]}»?`)) return;
    setBulkStatusApplying(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/seo-texts/bulk-status`, {
        method: "PATCH",
        body: JSON.stringify({ urls: Array.from(selectedUrls), status: newStatus }),
      });
      const d = await r.json();
      if (!r.ok) { flash("Помилка: " + (d.error || r.status)); }
      else {
        flash(`Статус оновлено для ${d.updated} текстів`);
        // refresh
        const rr = await apiFetch(`/api/projects/${activeId}/seo-texts`);
        const rows = await rr.json();
        reloadStatusMaps(rows);
        setSelectedUrls(new Set());
      }
    } catch (e) { flash("Помилка: " + e.message); }
    setBulkStatusApplying(false);
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      // Save first
      const htmlContent = getHtml();
      const plainText = getPlainText();
      await apiFetch(`/api/projects/${activeId}/seo-texts`, {
        method: "PUT",
        body: JSON.stringify({ url: selectedUrl, html_content: htmlContent, plain_text: plainText, seo_score: seoScore }),
      });
      // Then publish
      const r = await apiFetch(`/api/projects/${activeId}/seo-texts/publish`, {
        method: "POST",
        body: JSON.stringify({ url: selectedUrl, read_more: readMore }),
      });
      const d = await r.json();
      if (d.ok) {
        setPublishedUrls(prev => new Set([...prev, selectedUrl]));
        flash("Текст опубліковано на сайті через SDK");
      } else flash("Помилка: " + (d.error || ""));
    } catch (e) { flash("Помилка: " + e.message); }
    setPublishing(false);
  };

  const handleUnpublish = async () => {
    try {
      await apiFetch(`/api/projects/${activeId}/seo-texts/unpublish`, {
        method: "POST",
        body: JSON.stringify({ url: selectedUrl }),
      });
      setPublishedUrls(prev => { const n = new Set(prev); n.delete(selectedUrl); return n; });
      flash("Текст знято з публікації");
    } catch (e) { flash("Помилка: " + e.message); }
  };

  // AI auto-fix text
  const aiFixText = async () => {
    if (!editorRef.current) return;
    if (!getPlainText().trim()) { flash("Немає тексту"); return; }

    const TARGET_SCORE = 70;   // green zone threshold (matches scoreColor)
    const MAX_ITER = 5;
    const NO_PROGRESS_LIMIT = 2; // stop if score didn't improve N iterations in a row

    setAiFixing(true);
    let prevScore = seoScoreRef.current?.total || 0;
    let stagnant = 0;
    let bestHtml = getHtml();
    let bestScore = prevScore;
    let stoppedReason = "";

    try {
      for (let iter = 1; iter <= MAX_ITER; iter++) {
        setAiFixIter({ current: iter, max: MAX_ITER, score: prevScore });

        const html = getHtml();
        const problems = (seoScoreRef.current?.checks || [])
          .filter(c => c.status === "fail" || c.status === "warn")
          .map(c => `${c.name}: ${c.detail}`);

        // If no problems and we already hit green — bail before spending tokens.
        if (!problems.length && prevScore >= TARGET_SCORE) { stoppedReason = "already-green"; break; }

        const resp = await apiFetch(`/api/projects/${activeId}/seo-texts/ai-fix`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ html, url: selectedUrl, problems }),
        });
        if (!resp.ok) {
          const eb = await resp.text();
          flash("AI fix error: " + (eb.slice(0, 120) || resp.status));
          stoppedReason = "api-error";
          break;
        }
        const d = await resp.json();
        const fixed = (d.html || "").trim();
        if (!fixed || fixed.length < 50) { stoppedReason = "empty-response"; break; }

        editorRef.current.innerHTML = fixed;
        runSeoAnalysis();
        // Wait for runSeoAnalysis() debounce (500ms) + render cycle
        await new Promise(r => setTimeout(r, 700));
        const newScore = seoScoreRef.current?.total || 0;

        if (newScore > bestScore) { bestScore = newScore; bestHtml = fixed; }
        if (newScore >= TARGET_SCORE) { stoppedReason = "green"; prevScore = newScore; break; }

        // Detect stagnation / regression — protect from drift
        if (newScore <= prevScore) {
          stagnant++;
          if (stagnant >= NO_PROGRESS_LIMIT) { stoppedReason = "no-progress"; break; }
        } else {
          stagnant = 0;
        }
        prevScore = newScore;
      }

      // Restore best version (in case last iter regressed)
      if (editorRef.current.innerHTML !== bestHtml && bestScore > (seoScoreRef.current?.total || 0)) {
        editorRef.current.innerHTML = bestHtml;
        runSeoAnalysis();
        await new Promise(r => setTimeout(r, 700));
      }

      autoSave();
      const finalScore = seoScoreRef.current?.total || bestScore;
      const msgMap = {
        "green": `✓ Зелена зона (${finalScore})`,
        "no-progress": `⚠ Зупинено: оцінка не росте (${finalScore})`,
        "api-error": `⚠ Помилка API на ітерації`,
        "empty-response": `⚠ AI повернув порожнє`,
        "already-green": `✓ Вже в зеленій зоні (${finalScore})`,
        "": `Завершено (${MAX_ITER} ітерацій, оцінка ${finalScore})`,
      };
      flash(msgMap[stoppedReason] || msgMap[""]);
    } catch (e) {
      flash("Помилка: " + e.message);
    }
    setAiFixIter(null);
    setAiFixing(false);
  };

  // Internal uniqueness
  const checkInternal = async () => {
    setCheckingInternal(true);
    try {
      const resp = await apiFetch(`/api/projects/${activeId}/seo-texts/check-internal`, {
        method: "POST",
        body: JSON.stringify({ url: selectedUrl, plain_text: getPlainText() }),
      });
      const data = await resp.json();
      setUniquenessResult(prev => ({ ...(prev || {}), internal: data }));
    } catch {
      flash("Помилка перевірки");
    }
    setCheckingInternal(false);
  };

  // External uniqueness
  const checkExternal = async () => {
    setCheckingExternal(true);
    try {
      const resp = await apiFetch(`/api/projects/${activeId}/seo-texts/check-uniqueness`, {
        method: "POST",
        body: JSON.stringify({ plain_text: getPlainText() }),
      });
      const data = await resp.json();
      setUniquenessResult(prev => ({ ...(prev || {}), external: data }));
    } catch {
      flash("Помилка перевірки");
    }
    setCheckingExternal(false);
  };

  // Sync single page
  const syncPage = async (url) => {
    setPageStats(prev => ({ ...prev, [url]: { ...(prev[url] || {}), syncing: true } }));
    try {
      const r = await apiFetch(`/api/projects/${activeId}/seo-texts/sync-page`, {
        method: "POST", body: JSON.stringify({ url }),
      });
      const d = await r.json();
      if (d.error) { flash(d.error); }
      else { setPageStats(prev => ({ ...prev, [url]: { ...d, syncing: false } })); }
    } catch (e) { flash("Помилка: " + e.message); }
    setPageStats(prev => ({ ...prev, [url]: { ...(prev[url] || {}), syncing: false } }));
  };

  // Sync all pages
  const syncAllPages = async () => {
    setSyncingAll(true);
    for (const url of urls) {
      await syncPage(url);
    }
    setSyncingAll(false);
    flash(`Синхронізовано ${urls.length} сторінок`);
  };

  const validateLinks = async () => {
    setValidatingLinks(true); setLinkReport(null);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/seo-texts/validate-links`);
      const d = await r.json();
      if (r.ok) setLinkReport(d);
      else flash("Помилка: " + (d.error || "невідома"));
    } catch (e) { flash("Помилка: " + e.message); }
    setValidatingLinks(false);
  };

  const republishSelected = async () => {
    const urlsToSend = selectedUrls.size > 0 ? [...selectedUrls] : null;
    const label = urlsToSend ? `${urlsToSend.length} обраних` : "ВСІ опубліковані";
    if (!confirm(`Переопублікувати ${label}? Тексти отримають новий published_at, а кеш widget'а у відвідувачів сайту буде скинуто.`)) return;
    setRepublishing(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/seo-texts/republish`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ urls: urlsToSend }),
      });
      const d = await r.json();
      if (d.ok) {
        flash(`Переопубліковано: ${d.updated} текстів · версія widget'а v${d.newVersion}`);
      } else {
        flash("Помилка: " + (d.error || "невідома"));
      }
    } catch (e) { flash("Помилка: " + e.message); }
    setRepublishing(false);
  };

  const stripBadLinks = async () => {
    if (!confirm(`Прибрати ${linkReport?.totalBad || 0} битих лінків? Тег <a> зніметься, текст залишиться.`)) return;
    setStripping(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/seo-texts/strip-bad-links`, { method: "POST" });
      const d = await r.json();
      if (d.ok) {
        flash(`Очищено: ${d.anchorsStripped} лінків в ${d.updated} текстах`);
        await validateLinks(); // re-check
      } else flash("Помилка: " + (d.error || "невідома"));
    } catch (e) { flash("Помилка: " + e.message); }
    setStripping(false);
  };

  const freqBadge = (label, color, count) => (
    <span style={{ display: "inline-block", background: color, color: "#fff", borderRadius: 8, padding: "1px 7px", fontSize: 10, fontWeight: 600, marginRight: 4 }}>
      {label} ({count})
    </span>
  );

  const scoreColor = (total) => total >= 70 ? "#16a34a" : total >= 40 ? "#f59e0b" : "#ef4444";

  // ─── Вікі-лінковка: авто-перелінковка тексту по ключах кластерів ───
  // Матчить ключі і в точній формі, і за словоформами (укр/рос морфологія);
  // НЕ ставить посилання на поточну сторінку (self-link); по 1 лінку на URL.
  const wikiLink = () => {
    if (!editorRef.current) return;
    if (!selectedUrl) { flash("Спершу відкрийте текст сторінки."); return; }

    // стемінг: прибрати один найдовший словозмінний суфікс (укр+рос)
    const ENDINGS = [
      "ування","уванні","ованих","ованої","ськими","ністю",
      "ами","ями","ових","нього","ього","ому","ему","их","іх","ів","ей","ам","ям","ах","ях","ою","ею","еві",
      "ий","ій","ая","яя","ое","ее","ую","юю","им","ым","ом","ем","ья","ью","ье",
      "а","я","у","ю","е","о","и","і","ы","ь","й"
    ];
    const stem = (w) => {
      w = w.toLowerCase().replace(/['’ʼ]/g, "");
      for (const e of ENDINGS) {
        if (w.length - e.length >= 3 && w.endsWith(e)) return w.slice(0, -e.length);
      }
      return w;
    };
    const tokenize = (s) => {
      const out = [];
      const re = /[0-9A-Za-zЀ-ӿ'’ʼ]+/g;
      let m;
      while ((m = re.exec(s))) out.push({ text: m[0], start: m.index, end: m.index + m[0].length, stem: stem(m[0]) });
      return out;
    };

    const selN = normU(selectedUrl);
    const seen = {};
    const cands = [];
    for (const c of clusters) {
      if (!c.url || normU(c.url) === selN) continue; // no self-link
      for (const k of (c.keywords || [])) {
        const kw = (typeof k === "string" ? k : (k.keyword || k.name || "")).trim();
        if (kw.length < 4) continue;
        const words = tokenize(kw).map(x => x.stem).filter(Boolean);
        if (!words.length) continue;
        const key = c.url + "|" + kw.toLowerCase();
        if (seen[key]) continue;
        seen[key] = 1;
        cands.push({ words, wc: words.length, url: c.url, frequency: (k.frequency || k.freq || 0) });
      }
    }
    // довші фрази першими, далі за частотою
    cands.sort((a, b) => b.wc - a.wc || b.frequency - a.frequency);

    const SKIP = new Set(["A", "H1", "H2", "H3", "H4", "CODE", "PRE"]);
    const collectNodes = () => {
      const nodes = [];
      const walker = document.createTreeWalker(editorRef.current, NodeFilter.SHOW_TEXT, {
        acceptNode(n) {
          if (!n.textContent || !n.textContent.trim()) return NodeFilter.FILTER_REJECT;
          let p = n.parentElement;
          while (p && p !== editorRef.current) { if (SKIP.has(p.tagName)) return NodeFilter.FILTER_REJECT; p = p.parentElement; }
          return NodeFilter.FILTER_ACCEPT;
        }
      });
      while (walker.nextNode()) nodes.push(walker.currentNode);
      return nodes;
    };

    const MAX = 12;
    const usedUrls = new Set();
    let linked = 0;

    for (const cand of cands) {
      if (linked >= MAX) break;
      if (usedUrls.has(normU(cand.url))) continue;
      const nodes = collectNodes();
      let done = false;
      for (const node of nodes) {
        const toks = tokenize(node.textContent);
        if (toks.length < cand.wc) continue;
        for (let i = 0; i + cand.wc <= toks.length; i++) {
          let ok = true;
          for (let j = 0; j < cand.wc; j++) { if (toks[i + j].stem !== cand.words[j]) { ok = false; break; } }
          if (!ok) continue;
          const s = toks[i].start, e = toks[i + cand.wc - 1].end;
          const txt = node.textContent;
          const a = document.createElement("a");
          a.href = cand.url;
          a.textContent = txt.slice(s, e);
          const frag = document.createDocumentFragment();
          if (s > 0) frag.appendChild(document.createTextNode(txt.slice(0, s)));
          frag.appendChild(a);
          if (e < txt.length) frag.appendChild(document.createTextNode(txt.slice(e)));
          node.parentNode.replaceChild(frag, node);
          usedUrls.add(normU(cand.url));
          linked++;
          done = true;
          break;
        }
        if (done) break;
      }
    }

    if (linked > 0) {
      runSeoAnalysis();
      autoSave();
      flash(`🔗 Вікі-лінковка: додано ${linked} внутрішніх посилань`);
    } else {
      flash("Не знайдено ключів для лінковки в тексті");
    }
  };

  const checkIcon = (status) => status === "ok" ? "✓" : status === "warn" ? "⚠" : "✕";
  const checkColor = (status) => status === "ok" ? "#16a34a" : status === "warn" ? "#f59e0b" : "#ef4444";

  const toolbarBtn = (label, cmd) => (
    <button
      key={label}
      onClick={() => execCmd(cmd)}
      style={{ ...btnSm, minWidth: 32, fontWeight: 700, fontFamily: "inherit" }}
    >
      {label}
    </button>
  );

  // ═══ PAGE LIST VIEW ═══
  if (!selectedUrl) {
    const getKwCount = (url) => {
      const kws = new Set();
      for (const c of clusters) {
        if (c.url !== url) continue;
        for (const k of (c.keywords || [])) {
          const kw = typeof k === "string" ? k : (k.keyword || k.name || "");
          if (kw) kws.add(kw.toLowerCase());
        }
      }
      return kws.size;
    };

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>SEO Редактор</h2>
          <span style={{ fontSize: 12, color: t.textMuted }}>
            {urls.length} сторінок
            {urls.length > 0 && (
              <> · <span style={{ color: "#10b981", fontWeight: 600 }}>{urls.filter(u => publishedUrls.has(u)).length} опубл.</span>
              <> · <span style={{ color: "#f59e0b", fontWeight: 600 }}>{urls.filter(u => !publishedUrls.has(u) && !!savedTexts[u]?.plain_text).length} чернеток</span></></>
            )}
          </span>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            style={{ ...inp, fontSize: 11, padding: "5px 8px", marginLeft: 8, cursor: "pointer" }}>
            <option value="default">Сортування: за замовчуванням</option>
            <option value="withText">Сортування: спочатку з текстом</option>
            <option value="withoutText">Сортування: спочатку без тексту</option>
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            title="Фільтр за статусом"
            style={{ ...inp, fontSize: 11, padding: "5px 8px", cursor: "pointer", fontWeight: statusFilter !== "all" ? 600 : 400 }}>
            <option value="all">Статус: усі</option>
            <option value="draft">📝 Чернетка</option>
            <option value="pending_client">📤 На погодженні у клієнта</option>
            <option value="approved">✅ Погоджено клієнтом</option>
            <option value="rejected">❌ Відхилено (переписати)</option>
            <option value="none">— Без статусу</option>
            <option value="published">🟢 Опубліковано на сайті</option>
            <option value="unpublished">⚪ Не опубліковано</option>
          </select>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {selectedUrls.size === 0 && (
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: t.textMuted, cursor: "pointer", userSelect: "none" }}>
                <input type="checkbox" checked={bulkOverwrite} onChange={e => setBulkOverwrite(e.target.checked)} disabled={bulkTask?.status === "in_progress"} style={{ cursor: "pointer" }} />
                Перегенерувати існуючі
              </label>
            )}
            {selectedUrls.size > 0 && (
              <>
                <button
                  onClick={() => setSelectedUrls(new Set())}
                  style={{ ...btnG, fontSize: 11, padding: "6px 10px" }}
                >
                  Скинути вибір
                </button>
                <select
                  value=""
                  disabled={bulkStatusApplying}
                  onChange={e => { if (e.target.value) applyBulkStatus(e.target.value); e.target.value = ""; }}
                  title={`Змінити статус ${selectedUrls.size} вибраних текстів`}
                  style={{
                    fontSize: 11, padding: "6px 10px", borderRadius: 6,
                    border: `1px solid ${t.border}`, background: dark ? "#1a1a2e" : "#fff", color: t.text,
                    cursor: bulkStatusApplying ? "wait" : "pointer", fontWeight: 600,
                  }}>
                  <option value="">{bulkStatusApplying ? "Зберігаю..." : `Статус для ${selectedUrls.size} →`}</option>
                  <option value="draft">📝 Чернетка</option>
                  <option value="pending_client">📤 На погодженні у клієнта</option>
                  <option value="approved">✅ Погоджено клієнтом</option>
                  <option value="rejected">❌ Відхилено (переписати)</option>
                </select>
                <button
                  onClick={openAssignModal}
                  title={`Передати ${selectedUrls.size} сторінок копірайтеру (зі снапшотом ТЗ)`}
                  style={{ fontSize: 11, padding: "6px 12px", borderRadius: 6, border: "none", fontWeight: 700, color: "#fff", background: "linear-gradient(135deg,#16a34a,#22c55e)", cursor: "pointer" }}>
                  ✍ Передати копірайтеру · {selectedUrls.size}
                </button>
                <button
                  onClick={sendToFactory}
                  title={`Передати ${selectedUrls.size} сторінок у Контент-завод (конвеєр ботів)`}
                  style={{ fontSize: 11, padding: "6px 12px", borderRadius: 6, border: "none", fontWeight: 700, color: "#fff", background: "linear-gradient(135deg,#6366f1,#a855f7)", cursor: "pointer" }}>
                  🏭 У Контент-завод · {selectedUrls.size}
                </button>
              </>
            )}
            <button
              onClick={startBulkGenerate}
              disabled={bulkStarting || bulkTask?.status === "in_progress"}
              title={selectedUrls.size > 0
                ? `Згенерувати тексти з 0 для ${selectedUrls.size} вибраних сторінок через OpenAI gpt-4o`
                : "Генерує SEO-тексти для всіх сторінок з мета-тегами через OpenAI gpt-4o за тим самим ТЗ, що використовується кнопкою «Копіювати ТЗ»"}
              style={{
                background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff", border: "none",
                borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                opacity: (bulkStarting || bulkTask?.status === "in_progress") ? 0.6 : 1,
                fontFamily: "inherit",
              }}
            >
              {bulkStarting ? "..." : (bulkTask?.status === "in_progress" ? "Генерую..." : (selectedUrls.size > 0 ? `✨ Згенерувати ${selectedUrls.size} (OpenAI)` : "✨ Згенерувати все (OpenAI)"))}
            </button>
            <input
              value={pullSelector}
              onChange={e => savePullSelector(e.target.value)}
              placeholder="CSS-селектор тексту (напр. .seo_text)"
              title="Якщо на сайті немає розмітки data-spalah — вкажіть CSS-селектор блоку з текстом (напр. .seo_text для zoodom). Зберігається для цього проєкту."
              style={{ border: `1px solid ${t.inputBorder || t.border}`, background: t.inputBg || t.bgCard, color: t.text,
                borderRadius: 8, padding: "8px 10px", fontSize: 12, width: 210, fontFamily: "inherit" }}
            />
            <button onClick={exportDocx} title="Вивантажити відкритий текст як .docx" style={{ background: "#1f2937", color: "#fff", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>⬇ Word</button>
            <button onClick={exportAllZip} title="Вивантажити всі написані/відредаговані в Spalah тексти у ZIP" style={{ background: "#374151", color: "#fff", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>⬇ Всі у ZIP</button>
            <button
              onClick={pullFromSite}
              disabled={pulling}
              title={pullSelector
                ? `Підтягнути текст: спершу data-spalah, інакше по селектору «${pullSelector}»`
                : "Підтягнути текст: блок data-spalah, інакше основний контент сторінки (raw). Можна задати свій CSS-селектор зліва."}
              style={{
                background: "linear-gradient(135deg, #0ea5e9, #2563eb)", color: "#fff", border: "none",
                borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                opacity: pulling ? 0.6 : 1, fontFamily: "inherit",
              }}
            >
              {pulling ? "Тягну..." : (selectedUrls.size > 0 ? `⬇ Підтягнути з сайту ${selectedUrls.size}` : "⬇ Підтягнути з сайту")}
            </button>
            <button onClick={syncAllPages} disabled={syncingAll} style={{ ...btnP, fontSize: 12, opacity: syncingAll ? 0.6 : 1 }}>
              {syncingAll ? "Синхронізую..." : "Синхронізувати все"}
            </button>
            <button onClick={validateLinks} disabled={validatingLinks} title="Знайти биті лінки в усіх опублікованих текстах"
              style={{ ...btnG, fontSize: 12, opacity: validatingLinks ? 0.6 : 1 }}>
              {validatingLinks ? "Перевіряю..." : "🔍 Перевірити лінки"}
            </button>
            <button onClick={republishSelected} disabled={republishing}
              title={selectedUrls.size > 0 ? `Переопублікувати ${selectedUrls.size} обраних + скинути кеш на сайтах` : "Переопублікувати ВСІ опубліковані + скинути кеш"}
              style={{ background: "linear-gradient(135deg, #6366f1, #a855f7)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", opacity: republishing ? 0.6 : 1, fontFamily: "inherit" }}>
              {republishing ? "..." : (selectedUrls.size > 0 ? `↻ Переопублікувати ${selectedUrls.size}` : "↻ Переопублікувати все")}
            </button>
          </div>
        </div>

        {/* Pull-from-site progress / results panel */}
        {pullResult && (() => {
          const pr = pullResult;
          const pct = pr.total ? Math.round(pr.done / pr.total * 100) : 0;
          const STC = { pulled: ["#16a34a", "✅ data-spalah"], raw: ["#0ea5e9", "⬇ текст (raw)"], empty: ["#9ca3af", "порожній слот"], noMarkup: ["#f59e0b", "без тексту"], error: ["#ef4444", "помилка"] };
          return (
            <div style={{ ...card, padding: "12px 16px", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                <strong style={{ fontSize: 13, color: t.text }}>
                  {pr.running ? `⬇ Підтягую… ${pr.done}/${pr.total} (${pct}%)` : `✅ Підтягнуто ${pr.done}/${pr.total}`}
                </strong>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", fontSize: 11.5 }}>
                  <span style={{ color: "#16a34a", fontWeight: 700 }}>data-spalah: {pr.pulled}</span>
                  <span style={{ color: "#0ea5e9", fontWeight: 700 }}>raw/селектор: {pr.pulledRaw}</span>
                  <span style={{ color: "#9ca3af" }}>порожніх: {pr.emptySlots}</span>
                  <span style={{ color: "#f59e0b" }}>без тексту: {pr.noMarkup}</span>
                  <span style={{ color: "#ef4444" }}>помилок: {pr.errors}</span>
                  {!pr.running && <button onClick={() => setPullResult(null)} style={{ ...btnG, fontSize: 11, padding: "2px 8px" }}>✕</button>}
                </div>
              </div>
              <div style={{ height: 5, background: t.border, borderRadius: 3, overflow: "hidden", marginBottom: 8 }}>
                <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg,#0ea5e9,#2563eb)", transition: "width .2s" }} />
              </div>
              <div style={{ maxHeight: 200, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
                {pr.items.slice().reverse().slice(0, 200).map((it, i) => {
                  const sc = STC[it.status] || ["#9ca3af", it.status];
                  return (
                    <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 11.5 }}>
                      <span style={{ color: sc[0], fontWeight: 600, minWidth: 110 }}>{sc[1]}</span>
                      <span style={{ color: t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }} title={it.url}>{(it.url || "").replace(/^https?:\/\/[^/]+/, "") || "/"}</span>
                      {it.count != null && <span style={{ color: t.text, fontSize: 10 }}>{it.count} бл.</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Bad links report */}
        {linkReport && (
          <div style={{ ...card, padding: "14px 18px", marginBottom: 12, borderColor: linkReport.totalBad > 0 ? "#ef4444" : "#10b981" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: linkReport.totalBad > 0 ? "#ef4444" : "#10b981" }}>
                  {linkReport.totalBad > 0
                    ? `❌ Знайдено ${linkReport.totalBad} битих лінків у ${linkReport.textsWithBadLinks} текстах`
                    : `✓ Всі лінки валідні (${linkReport.totalOk} перевірено)`}
                </div>
                <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
                  Перевірено {linkReport.textsScanned} текстів, whitelist {linkReport.whitelistSize} URL з clusters
                </div>
              </div>
              {linkReport.totalBad > 0 && (
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={stripBadLinks} disabled={stripping} style={{ ...btnP, fontSize: 12, opacity: stripping ? 0.6 : 1 }}>
                    {stripping ? "Чищу..." : `🧹 Прибрати биті лінки (${linkReport.totalBad})`}
                  </button>
                  <button onClick={() => setLinkReport(null)} style={{ ...btnSm, fontSize: 12 }}>×</button>
                </div>
              )}
            </div>
            {linkReport.results?.length > 0 && (
              <div style={{ marginTop: 10, maxHeight: 280, overflowY: "auto", border: `1px solid ${t.border}`, borderRadius: 8 }}>
                {linkReport.results.map((r, i) => (
                  <div key={i} style={{ padding: "8px 12px", borderBottom: `1px solid ${t.border}`, fontSize: 12 }}>
                    <div style={{ fontFamily: "monospace", fontSize: 11, color: t.textMuted, marginBottom: 4 }}>{r.textUrl}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {r.bad.map((b, j) => (
                        <span key={j} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: dark ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)", color: "#ef4444", fontFamily: "monospace" }}>
                          «{b.anchor}» → {b.href}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Модалка «Побажання до генерації» */}
        {showAssignModal && (
          <div onClick={() => !assigning && setShowAssignModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ ...card, width: 480, maxWidth: "100%", padding: 22 }}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>✍ Передати копірайтерам</div>
              <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 14 }}>{selectedUrls.size} сторінок → у пул проєкту. Копірайтер сам візьме проєкт. ТЗ (ключі, n-грами, обсяг) додається автоматично.</div>
              <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, display: "block", marginBottom: 4 }}>Дедлайн (необов'язково)</label>
              <input type="date" value={assignDeadline} onChange={e => setAssignDeadline(e.target.value)} style={{ ...inp, width: "100%", marginBottom: 12 }} />
              <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, display: "block", marginBottom: 4 }}>Бриф / побажання (необов'язково)</label>
              <textarea value={assignNote} onChange={e => setAssignNote(e.target.value)} placeholder="Тон, акценти, що обов'язково згадати…" style={{ ...inp, width: "100%", minHeight: 70, marginBottom: 16, resize: "vertical", fontFamily: "inherit" }} />
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button onClick={() => setShowAssignModal(false)} disabled={assigning} style={{ ...btnG }}>Скасувати</button>
                <button onClick={submitAssign} disabled={assigning} style={{ ...btnP, opacity: assigning ? 0.6 : 1 }}>{assigning ? "Передаю…" : `Передати ${selectedUrls.size} →`}</button>
              </div>
            </div>
          </div>
        )}

        {showGenModal && (
          <div onClick={() => setShowGenModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ ...card, width: 580, maxWidth: "100%", padding: 22 }}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Побажання до генерації тексту</div>
              <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 12 }}>
                {selectedUrls.size > 0 ? `${selectedUrls.size} вибраних сторінок` : (bulkOverwrite ? `всі ${urls.length} сторінок (перезапис)` : `пусті сторінки (з ${urls.length})`)} · OpenAI gpt-4o
              </div>
              <textarea
                value={genRequirements}
                onChange={e => setGenRequirements(e.target.value)}
                autoFocus
                placeholder={"Напр.: згадати безкоштовну діагностику; тон дружній, без канцеляриту; додати блок про терміни виконання; гарантія 12 міс; уникати слова «дешево»; згадати оплату частинами…"}
                rows={7}
                style={{ ...inp, width: "100%", boxSizing: "border-box", resize: "vertical", fontSize: 13, lineHeight: 1.5 }}
              />
              <div style={{ fontSize: 11, color: t.textMuted, marginTop: 6 }}>
                Додаються до промпту з найвищим пріоритетом і зберігаються для проєкту. Порожнє поле — генерація за стандартним ТЗ.
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, paddingTop: 14, borderTop: `1px solid ${t.line || "rgba(255,255,255,0.08)"}` }}>
                <span style={{ fontSize: 12.5, fontWeight: 600 }}>Внутрішні посилання:</span>
                <select value={genLinkCount} onChange={e => setGenLinkCount(Number(e.target.value))} style={{ ...inp, padding: "7px 10px", fontSize: 13, width: "auto", cursor: "pointer" }}>
                  <option value={0}>Без посилань</option>
                  <option value={2}>До 2</option>
                  <option value={3}>До 3 (рекомендовано)</option>
                  <option value={5}>До 5</option>
                </select>
                <span style={{ fontSize: 11, color: t.textMuted }}>щоб не переспамлювати анкорами</span>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
                <button onClick={() => setShowGenModal(false)} style={{ ...btnG, padding: "8px 14px" }}>Скасувати</button>
                {genRequirements.trim() && <button onClick={() => setGenRequirements("")} style={{ ...btnG, padding: "8px 14px" }}>Очистити</button>}
                <button onClick={runBulkGenerate} disabled={bulkStarting} style={{ ...btnP, padding: "8px 18px", fontWeight: 600 }}>
                  {bulkStarting ? "Запускаю…" : "✨ Згенерувати"}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Bulk-generate progress strip */}
        {bulkTask && bulkTask.status === "in_progress" && (() => {
          const p = bulkTask.progress || {};
          const total = p.total || 0;
          const done = p.done || 0;
          const pct = total > 0 ? Math.round((done / total) * 100) : 0;
          return (
            <div style={{ ...card, padding: "12px 16px", marginBottom: 12, borderColor: "#10b981", background: dark ? "rgba(16,185,129,0.06)" : "rgba(16,185,129,0.04)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Генерація SEO-текстів через OpenAI</span>
                <span style={{ fontSize: 11, color: t.textMuted }}>
                  {done} / {total} · OK: {p.ok || 0}{p.errors ? ` · помилок: ${p.errors}` : ""}
                </span>
              </div>
              <div style={{ height: 6, borderRadius: 3, overflow: "hidden", background: dark ? "#252535" : "#e5e7eb" }}>
                <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg, #10b981, #059669)", transition: "width 0.4s ease" }} />
              </div>
            </div>
          );
        })()}
        {bulkTask && (bulkTask.status === "done" || bulkTask.status === "completed" || bulkTask.status === "success") && bulkTask.progress && (
          <div style={{ ...card, padding: "10px 14px", marginBottom: 12, fontSize: 12 }}>
            <strong>Останній прогон:</strong> {bulkTask.progress.ok || 0} згенеровано, {bulkTask.progress.errors || 0} помилок
            {bulkTask.finished_at && <span style={{ color: t.textMuted, marginLeft: 8 }}>· {new Date(bulkTask.finished_at).toLocaleString("uk-UA")}</span>}
          </div>
        )}
        {bulkTask && bulkTask.status === "failed" && (
          <div style={{ ...card, padding: "10px 14px", marginBottom: 12, borderColor: "#ef4444", color: "#ef4444", fontSize: 12 }}>
            Прогон провалився: {bulkTask.error || "невідома помилка"}
          </div>
        )}

        <div style={{ ...card, padding: "10px 12px", marginBottom: 8, display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", color: t.textMuted }}>+ Сторінка:</span>
          <input
            value={addUrlInput}
            onChange={e => setAddUrlInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleAddCustomUrl()}
            placeholder="https://example.com/page — вручну"
            style={{ ...inp, fontSize: 12, flex: 1, height: 32 }}
          />
          <button onClick={handleAddCustomUrl} style={{ ...btnP, height: 32, whiteSpace: "nowrap", fontSize: 12 }}>Відкрити</button>
        </div>

        <div style={{ ...card, padding: 0, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...th, width: 36, textAlign: "center", padding: "8px 4px" }} title="Виділити для генерації OpenAI">
                  <input
                    type="checkbox"
                    checked={urls.length > 0 && selectedUrls.size === urls.length}
                    ref={el => { if (el) el.indeterminate = selectedUrls.size > 0 && selectedUrls.size < urls.length; }}
                    onChange={toggleSelectAll}
                    disabled={bulkTask?.status === "in_progress"}
                    style={{ cursor: "pointer" }}
                  />
                </th>
                <th style={{ ...th, textAlign: "left" }}>Сторінка</th>
                <th style={{ ...th, width: 80, textAlign: "center" }}>Ключів</th>
                <th style={{ ...th, width: 110, textAlign: "center" }} title="Сумарна частотність усіх ключів сторінки">Частотність</th>
                <th style={{ ...th, width: 80, textAlign: "center" }}>Оцінка</th>
                <th style={{ ...th, textAlign: "left" }}>Текст</th>
                <th style={{ ...th, width: 90, textAlign: "center" }}></th>
              </tr>
            </thead>
            <tbody>
              {urls.map((url, i) => {
                const kwCount = getKwCount(url);
                const stats = pageStats[url];
                const saved = savedTexts[url];
                const score = saved?.seo_score?.total;
                const hasText = !!saved?.plain_text;
                const freqSum = kwStatFor(url).freqSum;
                const displayH1 = (hasText ? (extractH1(saved?.html_content) || "") : "") || stats?.h1 || "";
                const preview = hasText ? (saved.plain_text || "").replace(/\s+/g, " ").trim().slice(0, 90) : "";
                const badges = (
                  <>
                    {hasText && <OriginBadge origin={saved?.origin} />}
                    <StatusBadge status={statusByUrl[url]} feedback={clientFeedback[url]} />
                    {factoryJobs[url] && <span onClick={factoryJobs[url].needs_editorial ? (e) => { e.stopPropagation(); openEditorial(url); } : undefined} title={(typeof factoryJobs[url].issues === "object" ? JSON.stringify(factoryJobs[url].issues) : "") + (factoryJobs[url].risk ? ` · ризик: ${factoryJobs[url].risk}` : "") + (factoryJobs[url].quality != null ? ` · якість ${factoryJobs[url].quality}` : "")} style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4, cursor: factoryJobs[url].needs_editorial ? "pointer" : "default", background: "rgba(99,102,241,0.12)", color: factoryJobs[url].status === "done" ? "#10b981" : (factoryJobs[url].status === "failed" || factoryJobs[url].status === "needs_human") ? "#ef4444" : "#6366f1" }}>{(FACTORY_STAGE[factoryJobs[url].stage] || factoryJobs[url].stage)}{factoryJobs[url].attempt > 1 && factoryJobs[url].status === "running" ? ` ·${factoryJobs[url].attempt}` : ""}{factoryJobs[url].needs_editorial ? " 📝" : ""}</span>}
                    <WriterBadge writerStatus={saved?.writer_status} writerName={saved?.writer_name} />
                  </>
                );
                const isSelected = selectedUrls.has(url);
                return (
                  <tr key={url} style={{
                    background: isSelected ? (dark ? "rgba(16,185,129,0.10)" : "rgba(16,185,129,0.06)") : (i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.015)")),
                    cursor: "pointer",
                  }}
                    onClick={() => setSelectedUrl(url)}>
                    <td style={{ ...td, textAlign: "center", padding: "6px 4px" }} onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleUrlSelect(url)}
                        disabled={bulkTask?.status === "in_progress"}
                        style={{ cursor: "pointer" }}
                      />
                    </td>
                    <td style={{ ...td, fontSize: 12 }}>
                      {displayH1 ? (
                        <div style={{ maxWidth: 420 }}>
                          <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }} title={displayH1}>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayH1}</span>
                            {badges}
                          </div>
                          <div style={{ fontSize: 10, color: t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={url}>
                            {url.replace(/^https?:\/\/[^/]+/, "")}
                          </div>
                        </div>
                      ) : (
                        <div style={{ fontWeight: 500, maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }} title={url}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{url.replace(/^https?:\/\/[^/]+/, "")}</span>
                          {badges}
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: "center", fontSize: 12 }}>{kwCount}</td>
                    <td style={{ ...td, textAlign: "center", fontSize: 12 }} title="Сумарна частотність усіх ключів сторінки">{freqSum ? freqSum.toLocaleString() : "—"}</td>
                    <td style={{ ...td, textAlign: "center" }} title={hasText ? "Оцінка тексту" : "Текст ще не написано"}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: !hasText ? t.textMuted : ((score || 0) >= 70 ? "#10b981" : (score || 0) >= 40 ? "#f59e0b" : "#ef4444") }}>{hasText && score != null ? score : 0}</span>
                    </td>
                    <td style={{ ...td, fontSize: 11, color: t.textMuted }}>
                      {preview
                        ? <span title={preview} style={{ display: "inline-block", maxWidth: 340, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "middle" }}>{preview}</span>
                        : <span style={{ color: t.textMuted }}>— ще не написано</span>}
                    </td>
                    <td style={{ ...td, textAlign: "center" }} onClick={e => e.stopPropagation()}>
                      {customUrls.includes(url) ? (
                        <button onClick={() => removeCustomUrl(url)} title="Прибрати" style={{ ...btnSm, fontSize: 10, padding: "3px 8px", color: "#ef4444", border: "none", background: "transparent" }}>✕</button>
                      ) : (
                        <button onClick={() => syncPage(url)} disabled={stats?.syncing} title="Синхронізувати сторінку" style={{ ...btnSm, fontSize: 10, padding: "3px 8px" }}>
                          {stats?.syncing ? "..." : "Синхр."}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {urls.length === 0 && (
          <div style={{ ...card, textAlign: "center", padding: 40 }}>
            <p style={{ fontWeight: 600, marginBottom: 8 }}>Немає сторінок</p>
            <p style={{ fontSize: 13, color: t.textMuted }}>Спершу завантажте кластери з ключовими словами</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
    {/* HTML Import Modal */}
    {showHtmlImport && (
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
        onClick={() => setShowHtmlImport(false)}>
        <div onClick={e => e.stopPropagation()} style={{ background: dark ? "#1a1a2e" : "#fff", borderRadius: 16, padding: 24, maxWidth: 700, width: "95%", maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Вставити HTML</div>
          <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 12 }}>
            Вставте HTML код — він буде перетворений у відформатований текст в редакторі
          </div>
          <textarea
            value={htmlImportText}
            onChange={e => setHtmlImportText(e.target.value)}
            placeholder={'<h2>Заголовок</h2>\n<p>Текст абзацу з <strong>жирним</strong> словом.</p>\n<ul>\n  <li>Пункт 1</li>\n  <li>Пункт 2</li>\n</ul>'}
            style={{ flex: 1, minHeight: 300, padding: 14, borderRadius: 8, border: `1px solid ${t.border}`, background: dark ? "#0d0d1a" : "#fafbfc", color: t.text, fontSize: 12, lineHeight: 1.5, fontFamily: "monospace", resize: "vertical" }}
            autoFocus
          />
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button onClick={() => {
              if (!editorRef.current || !htmlImportText.trim()) return;
              // Clean the HTML — keep only safe tags
              const tmp = document.createElement("div");
              tmp.innerHTML = htmlImportText;
              // Remove script, style, iframe
              tmp.querySelectorAll("script, style, iframe, link, noscript").forEach(el => el.remove());
              // Remove all classes, ids, styles except basic formatting
              const clean = (node) => {
                for (const child of [...node.children]) {
                  const tag = child.tagName.toLowerCase();
                  const allowed = ["h1","h2","h3","h4","p","br","strong","b","em","i","u","a","ul","ol","li","table","tr","td","th","thead","tbody","blockquote","img","figure","figcaption"];
                  if (!allowed.includes(tag)) {
                    // Replace with its content
                    while (child.firstChild) child.parentNode.insertBefore(child.firstChild, child);
                    child.remove();
                  } else {
                    // Remove all attributes except href, src, alt
                    const keepAttrs = ["href", "src", "alt"];
                    [...child.attributes].forEach(attr => {
                      if (!keepAttrs.includes(attr.name)) child.removeAttribute(attr.name);
                    });
                    clean(child);
                  }
                }
              };
              clean(tmp);
              editorRef.current.innerHTML = tmp.innerHTML;
              setShowHtmlImport(false);
              setHtmlImportText("");
              runSeoAnalysis();
              autoSave();
              flash("HTML вставлено");
            }} style={btnP}>Вставити в редактор</button>
            <button onClick={() => {
              if (!editorRef.current || !htmlImportText.trim()) return;
              // Append instead of replace
              const tmp = document.createElement("div");
              tmp.innerHTML = htmlImportText;
              tmp.querySelectorAll("script, style, iframe, link, noscript").forEach(el => el.remove());
              editorRef.current.innerHTML += tmp.innerHTML;
              setShowHtmlImport(false);
              setHtmlImportText("");
              runSeoAnalysis();
              autoSave();
              flash("HTML додано до тексту");
            }} style={btnG}>Додати до існуючого</button>
            <button onClick={() => { setShowHtmlImport(false); setHtmlImportText(""); }} style={btnSm}>Скасувати</button>
          </div>
        </div>
      </div>
    )}

    {/* HTML Export/View Modal */}
    {showHtmlExport && (
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
        onClick={() => setShowHtmlExport(false)}>
        <div onClick={e => e.stopPropagation()} style={{ background: dark ? "#1a1a2e" : "#fff", borderRadius: 16, padding: 24, maxWidth: 700, width: "95%", maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>HTML код тексту</div>
          <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 12 }}>
            Готовий HTML для вставки на сайт
          </div>
          <textarea
            value={htmlImportText}
            readOnly
            style={{ flex: 1, minHeight: 300, padding: 14, borderRadius: 8, border: `1px solid ${t.border}`, background: dark ? "#0d0d1a" : "#fafbfc", color: t.text, fontSize: 12, lineHeight: 1.5, fontFamily: "monospace", resize: "vertical" }}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button onClick={() => { navigator.clipboard.writeText(htmlImportText); flash("HTML скопійовано"); }} style={btnP}>Копіювати HTML</button>
            <button onClick={() => setShowHtmlExport(false)} style={btnSm}>Закрити</button>
          </div>
        </div>
      </div>
    )}

    <div>
      {/* Header with back button */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <button onClick={() => { setSelectedUrl(""); lastUrlRef.current = ""; }} style={{ ...btnSm, fontSize: 12, padding: "4px 10px" }}>← Назад</button>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>SEO Редактор</h2>
        <span style={{ fontSize: 11, color: t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{selectedUrl.replace(/^https?:\/\/[^/]+/, "")}</span>
        <a href={selectedUrl} target="_blank" rel="noopener noreferrer" style={{ ...btnG, textDecoration: "none", whiteSpace: "nowrap", fontSize: 12 }}>Відкрити ↗</a>
      </div>

      {/* Page sync info */}
      {pageStats[selectedUrl] && (
        <div style={{ ...card, padding: "10px 14px", marginBottom: 10, fontSize: 11 }}>
          <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <span>На сайті: <strong>{pageStats[selectedUrl].charCount?.toLocaleString()}</strong> сим</span>
            <span>Ключів: <strong style={{ color: "#10b981" }}>{pageStats[selectedUrl].foundKeywords}</strong>/{pageStats[selectedUrl].totalKeywords}</span>
            <span>Без покриття: <strong style={{ color: pageStats[selectedUrl].missingKeywords > 0 ? "#ef4444" : "#10b981" }}>{pageStats[selectedUrl].missingKeywords}</strong></span>
            {pageStats[selectedUrl].links && (
              <>
                <span>Внутр. лінків: <strong style={{ color: "#6366f1" }}>{pageStats[selectedUrl].links.internalCount}</strong></span>
                <span>Зовн. лінків: <strong style={{ color: "#f59e0b" }}>{pageStats[selectedUrl].links.externalCount}</strong></span>
              </>
            )}
            <button onClick={() => syncPage(selectedUrl)} disabled={pageStats[selectedUrl]?.syncing} style={{ ...btnSm, fontSize: 10, marginLeft: "auto" }}>
              {pageStats[selectedUrl]?.syncing ? "..." : "Оновити"}
            </button>
          </div>

          {/* H1 + links details */}
          {(pageStats[selectedUrl].h1 || pageStats[selectedUrl].links) && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${t.border}`, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {pageStats[selectedUrl].h1 && (
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>H1 на сайті</div>
                  <div style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.3 }}>{pageStats[selectedUrl].h1}</div>
                  {hfKeyword && pageStats[selectedUrl].h1 && (
                    pageStats[selectedUrl].h1.toLowerCase().includes(hfKeyword.toLowerCase())
                      ? <div style={{ fontSize: 10, color: "#10b981", marginTop: 2 }}>✓ містить ВЧ-ключ</div>
                      : <div style={{ fontSize: 10, color: "#ef4444", marginTop: 2 }}>✗ не містить ВЧ-ключ "{hfKeyword}"</div>
                  )}
                </div>
              )}
              {pageStats[selectedUrl].links && (
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>
                    Посилання на сторінці ({pageStats[selectedUrl].links.internalCount + pageStats[selectedUrl].links.externalCount})
                  </div>
                  <div style={{ maxHeight: 110, overflowY: "auto", fontSize: 10, fontFamily: "monospace", lineHeight: 1.4 }}>
                    {(pageStats[selectedUrl].links.internal || []).slice(0, 30).map((l, i) => (
                      <div key={"i" + i} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={l.href}>
                        <span style={{ color: "#6366f1" }}>↪</span> {l.anchor || <span style={{ color: t.textMuted, fontStyle: "italic" }}>(без анкора)</span>}
                        <span style={{ color: t.textMuted }}> · {(() => { try { return new URL(l.href).pathname; } catch { return l.href; } })()}</span>
                      </div>
                    ))}
                    {(pageStats[selectedUrl].links.external || []).slice(0, 10).map((l, i) => (
                      <div key={"e" + i} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#f59e0b" }} title={l.href}>
                        ↗ {l.anchor || "(без анкора)"} <span style={{ color: t.textMuted }}>· {(() => { try { return new URL(l.href).host; } catch { return l.href; } })()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 3-column layout */}
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>

        {/* Left panel — ТЗ на текст */}
        <div style={{ width: 280, flexShrink: 0, maxHeight: "calc(100vh - 140px)", overflowY: "auto" }}>
          {/* ТЗ SEO */}
          <div style={{ ...card, marginBottom: 8 }}>
            <p style={{ fontSize: 12, fontWeight: 800, margin: "0 0 8px", color: "#6366f1", textTransform: "uppercase", letterSpacing: 0.5 }}>ТЗ — SEO вимоги</p>

            <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 6, wordBreak: "break-all" }}>{selectedUrl}</div>

            {/* HF keyword */}
            {hfKeyword && (
              <div style={{ padding: "6px 8px", borderRadius: 6, background: "rgba(239,68,68,0.08)", marginBottom: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#ef4444", marginBottom: 2 }}>ВЧ КЛЮЧ (обов'язковий)</div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{hfKeyword} <span style={{ color: t.textMuted, fontWeight: 400 }}>({pageKeywords[0]?.frequency || 0})</span></div>
                <div style={{ fontSize: 10, color: t.textMuted }}>Використати 2-4 рази, в першому абзаці, щільність &lt;3%</div>
              </div>
            )}

            {/* Volume */}
            <div style={{ padding: "6px 8px", borderRadius: 6, background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", marginBottom: 6 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted }}>ОБСЯГ ТЕКСТУ</div>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{recommendedLength.min}–{recommendedLength.max} символів</div>
            </div>

            {/* Structure requirements */}
            <div style={{ padding: "6px 8px", borderRadius: 6, background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", marginBottom: 6 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted }}>СТРУКТУРА</div>
              <div style={{ fontSize: 11 }}>H2 заголовки — мінімум 3-5 шт.</div>
              <div style={{ fontSize: 11 }}>H3 підзаголовки — для деталізації</div>
              <div style={{ fontSize: 11 }}>Абзаци — короткі, 2-4 речення</div>
            </div>

            {/* Keywords */}
            <div style={{ marginBottom: 6 }}>
              <div style={{ display: "flex", gap: 4, marginBottom: 4, flexWrap: "wrap", alignItems: "center" }}>
                {hfKeys.length > 0 && freqBadge("ВЧ", "#ef4444", hfKeys.length)}
                {mfKeys.length > 0 && freqBadge("СЧ", "#f59e0b", mfKeys.length)}
                {lfKeys.length > 0 && freqBadge("НЧ", "#3b82f6", lfKeys.length)}
                <span style={{ fontSize: 9, color: "#10b981", marginLeft: "auto" }}>зелені = вже в тексті</span>
              </div>
              <div style={{ maxHeight: 160, overflowY: "auto", fontSize: 10 }}>
                {pageKeywords.slice(0, 40).map((k, i) => {
                  const used = ngramCovered(k.keyword);
                  return (
                    <div key={i} title={used ? "вже використано в тексті" : "ще не використано"} style={{ display: "flex", justifyContent: "space-between", padding: "1px 0", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f5f5f5"}` }}>
                      <span style={{ color: used ? "#10b981" : undefined, fontWeight: used ? 700 : 400 }}>{used ? "✓ " : ""}{k.keyword}</span>
                      <span style={{ color: used ? "#10b981" : t.textMuted, marginLeft: 4 }}>{k.frequency}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Missing keywords from sync */}
            {pageStats[selectedUrl]?.missing?.length > 0 && (
              <div style={{ padding: "6px 8px", borderRadius: 6, background: "rgba(239,68,68,0.06)", marginBottom: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#ef4444", marginBottom: 2 }}>ВІДСУТНІ НА СТОРІНЦІ ({pageStats[selectedUrl].missing.length})</div>
                <div style={{ maxHeight: 120, overflowY: "auto", fontSize: 10 }}>
                  {pageStats[selectedUrl].missing.map((k, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "1px 0", borderBottom: `1px solid ${dark ? "#2a1a1a" : "#fde8e8"}` }}>
                      <span style={{ color: "#ef4444" }}>{k.keyword}</span>
                      <span style={{ color: t.textMuted }}>{k.frequency}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* N-grams */}
            {pageNgrams.length > 0 && (
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, marginBottom: 2 }}>N-ГРАМИ (використати в тексті) · <span style={{ color: "#10b981" }}>зелені = вже в тексті</span></div>
                <div style={{ maxHeight: 100, overflowY: "auto", fontSize: 10 }}>
                  {pageNgrams.slice(0, 15).map((g, i) => {
                    const used = ngramCovered(g.gram);
                    return (
                      <span key={i} title={used ? "вже використано в тексті" : "ще не використано"} style={{ display: "inline-block", padding: "1px 5px", margin: "1px 2px", borderRadius: 4, background: used ? "rgba(16,185,129,0.16)" : (g.priority === "high" ? "rgba(239,68,68,0.1)" : "rgba(245,158,11,0.1)"), color: used ? "#10b981" : (g.priority === "high" ? "#ef4444" : "#f59e0b"), fontWeight: used ? 700 : 400, fontSize: 10 }}>
                        {used ? "✓ " : ""}{g.gram}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ТЗ AEO */}
          <div style={{ ...card, marginBottom: 8, borderLeft: "3px solid #8b5cf6" }}>
            <p style={{ fontSize: 12, fontWeight: 800, margin: "0 0 8px", color: "#8b5cf6", textTransform: "uppercase", letterSpacing: 0.5 }}>ТЗ — AEO вимоги</p>

            <div style={{ fontSize: 11, lineHeight: 1.7 }}>
              <div style={{ padding: "4px 0", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f5f5f5"}` }}>
                <strong style={{ color: "#8b5cf6" }}>1.</strong> Перші 200 слів = <strong>answer capsule</strong> (пряма відповідь на запит)
              </div>
              <div style={{ padding: "4px 0", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f5f5f5"}` }}>
                <strong style={{ color: "#8b5cf6" }}>2.</strong> Починати з <strong>факту/відповіді</strong>, не з води ("у цій статті...")
              </div>
              <div style={{ padding: "4px 0", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f5f5f5"}` }}>
                <strong style={{ color: "#8b5cf6" }}>3.</strong> Речення <strong>до 17 слів</strong> (7 клас читабельності)
              </div>
              <div style={{ padding: "4px 0", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f5f5f5"}` }}>
                <strong style={{ color: "#8b5cf6" }}>4.</strong> <strong>1 факт/число на 80 слів</strong> мінімум (цифри, %, дати, метрики)
              </div>
              <div style={{ padding: "4px 0", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f5f5f5"}` }}>
                <strong style={{ color: "#8b5cf6" }}>5.</strong> <strong>Списки та таблиці</strong> — AI цитує структуровані дані в 4.1x частіше
              </div>
              <div style={{ padding: "4px 0", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f5f5f5"}` }}>
                <strong style={{ color: "#8b5cf6" }}>6.</strong> <strong>3+ внутрішніх посилань</strong> — AI використовує контекст лінків
              </div>
              <div style={{ padding: "4px 0", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f5f5f5"}` }}>
                <strong style={{ color: "#8b5cf6" }}>7.</strong> <strong>Alt на зображеннях</strong> — AI читає alt для розуміння контенту
              </div>
              <div style={{ padding: "4px 0", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f5f5f5"}` }}>
                <strong style={{ color: "#8b5cf6" }}>8.</strong> Оригінальні дані/дослідження — AI цитує <strong>унікальні джерела</strong>
              </div>
              <div style={{ padding: "4px 0", color: "#ef4444" }}>
                <strong>⛔</strong> <strong>БЕЗ FAQ-блоку</strong> — текст має бути повноцінним описом послуги/товару, а не Q&A
              </div>
            </div>
          </div>

          {/* Мета-теги сторінки */}
          {(() => {
            const cl = clusters.find(c => c.url === selectedUrl);
            const meta = cl?.meta;
            return meta ? (
              <div style={{ ...card, marginBottom: 8 }}>
                <p style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, margin: "0 0 4px", textTransform: "uppercase" }}>Мета-теги сторінки</p>
                <div style={{ fontSize: 11, marginBottom: 2 }}><strong>Title:</strong> {meta.title || "—"}</div>
                <div style={{ fontSize: 11, marginBottom: 2 }}><strong>H1:</strong> {meta.h1 || "—"}</div>
                <div style={{ fontSize: 11 }}><strong>Desc:</strong> {(meta.description || "—").slice(0, 120)}</div>
              </div>
            ) : null;
          })()}

          {/* Кнопка копіювання ТЗ */}
          <button onClick={() => {
            const cl = clusters.find(c => c.url === selectedUrl);
            const meta = cl?.meta || {};
            const kws = pageKeywords;
            const hf = kws.filter(k => k.frequency >= 100);
            const mf = kws.filter(k => k.frequency >= 20 && k.frequency < 100);
            const lf = kws.filter(k => k.frequency > 0 && k.frequency < 20);

            let tz = `════════════════════════════════════════
  ТЗ НА НАПИСАННЯ SEO+AEO ТЕКСТУ
════════════════════════════════════════

📌 СТОРІНКА: ${selectedUrl}
📌 ПРОЕКТ: ${proj.domain}
📌 ДАТА: ${new Date().toLocaleDateString("uk-UA")}
📌 МОВА ТЕКСТУ: ${({ uk: "українська", ukr: "українська", ua: "українська", ru: "русский", rus: "русский", en: "English", pl: "polski" })[(proj?.lang || "uk").toString().toLowerCase()] || (proj?.lang || "українська")}

────────────────────────────────────────
  МЕТА-ТЕГИ (НЕ ЗМІНЮВАТИ)
────────────────────────────────────────
Title: ${meta.title || "[не згенеровано]"}
H1: ${meta.h1 || "[не згенеровано]"}
Description: ${meta.description || "[не згенеровано]"}

────────────────────────────────────────
  SEO ВИМОГИ
────────────────────────────────────────
📏 Обсяг: ${recommendedLength.min}–${recommendedLength.max} символів без пробілів (СТРОГО, не менше ${recommendedLength.min})
   ⚠️ Це повноцінний опис послуги/товару. Розкривай тему детально, з фактами, прикладами і користю для клієнта.
   ⚠️ Якщо текст вийшов коротшим — додай ще H2-розділи (характеристики, кейси, етапи замовлення, гарантії).
📐 Структура:
   • H2 заголовки — мінімум 4-6 штук
   • H3 підзаголовки — для деталізації розділів
   • Абзаци — короткі, по 2-4 речення

🔑 ГОЛОВНИЙ КЛЮЧ (ВЧ): "${hfKeyword || "—"}" (${kws[0]?.frequency || 0})
   → Використати 2-4 рази
   → Обов'язково в першому абзаці
   → Щільність не більше 3%

🔑 СЕРЕДНЬОЧАСТОТНІ КЛЮЧІ (використати в тексті):
${mf.length ? mf.map(k => `   • ${k.keyword} (${k.frequency})`).join("\n") : "   [немає]"}

🔑 НИЗЬКОЧАСТОТНІ КЛЮЧІ (використати де доречно):
${lf.length ? lf.map(k => `   • ${k.keyword} (${k.frequency})`).join("\n") : "   [немає]"}
`;

            if (pageNgrams.length) {
              tz += `
📊 N-ГРАМИ (тематичні фрази — використати в тексті):
${pageNgrams.slice(0, 15).map(g => `   • ${g.gram}`).join("\n")}
`;
            }

            tz += `
────────────────────────────────────────
  AEO ВИМОГИ (для AI-пошуку)
────────────────────────────────────────
Ці вимоги ОБОВ'ЯЗКОВІ — вони впливають на те,
чи буде текст цитуватися в ChatGPT, Perplexity,
Gemini та інших AI-системах.

1️⃣ ПЕРШІ 200 СЛІВ = "ANSWER CAPSULE"
   Перший абзац — це пряма відповідь на запит
   користувача. AI витягує саме цю частину.
   Головний ключ ОБОВ'ЯЗКОВО в першому абзаці.

2️⃣ ПОЧИНАТИ З ФАКТУ, НЕ З ВОДИ
   ❌ "У цій статті ми розглянемо..."
   ❌ "Сьогодні ми поговоримо про..."
   ✅ "[Факт/відповідь]. [Деталі]. [Контекст]."

3️⃣ РЕЧЕННЯ ДО 17 СЛІВ
   Середня довжина речення — не більше 17 слів.
   Рівень читабельності — 7 клас.
   Уникати складнопідрядних речень.

4️⃣ ЩІЛЬНІСТЬ ФАКТІВ: 1 ФАКТ НА 80 СЛІВ
   Кожні 80 слів — мінімум 1 конкретне число,
   відсоток, дата або метрика.
   Приклади: "збільшує на 40%", "від 2500 грн",
   "за 3-5 днів", "понад 150 клієнтів".

5️⃣ СПИСКИ ТА ТАБЛИЦІ
   AI цитує структуровані дані в 4.1x частіше.
   Додати мінімум 1 список або таблицю з даними.

6️⃣ ВНУТРІШНІ ПОСИЛАННЯ
   Мінімум 3 посилання на інші сторінки сайту.
   AI використовує контекст лінків для розуміння.

7️⃣ ЗОБРАЖЕННЯ З ALT
   Кожне зображення повинно мати alt-текст.
   AI читає alt для розуміння контенту.

8️⃣ УНІКАЛЬНІ ДАНІ
   Якщо є можливість — додати оригінальні
   дослідження, статистику, порівняння.
   AI цитує унікальні джерела найчастіше.

⛔ БЕЗ FAQ-БЛОКУ
   НЕ додавати розділ "Часті запитання" / "FAQ" / Q&A.
   Це повноцінний опис послуги/товару, а не список
   питань-відповідей. Замість FAQ — додаткові H2 про
   характеристики, кейси, етапи замовлення, гарантії.

────────────────────────────────────────
  СТРУКТУРА ТЕКСТУ (РЕКОМЕНДОВАНА)
────────────────────────────────────────
H1: ${meta.h1 || "[головний ключ]"}

[Перший абзац — Answer Capsule, 40-60 слів]
[Пряма відповідь + головний ключ + 1-2 факти]

H2: [Тема розділу 1 з ключем]
[2-3 абзаци, списки, факти]

H2: [Тема розділу 2 з ключем]
[2-3 абзаци, таблиця з даними]

H2: [Тема розділу 3 з ключем]
[2-3 абзаци, внутрішні посилання]

H2: [Тема розділу 4 — переваги / характеристики / кейси]
[2-3 абзаци]

H2: [Тема розділу 5 — як замовити / етапи / умови]
[2-3 абзаци]

════════════════════════════════════════
  Spalah SEO+AEO Platform | ${proj.domain}
════════════════════════════════════════`;

            navigator.clipboard.writeText(tz);
            flash("ТЗ скопійовано — передайте копірайтеру");
          }} style={{
            width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid #6366f1`,
            background: "rgba(99,102,241,0.08)", color: "#6366f1", fontSize: 12, fontWeight: 700,
            cursor: "pointer", fontFamily: "inherit", transition: "all .15s",
          }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.15)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
          >
            📋 Копіювати ТЗ для копірайтера
          </button>
        </div>

        {/* Center — Editor */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...card, padding: 0 }}>
            {/* Toolbar */}
            <div style={{ display: "flex", gap: 4, padding: "8px 12px", borderBottom: `1px solid ${t.border}`, flexWrap: "wrap", alignItems: "center" }}>
              {toolbarBtn("H2", "H2")}
              {toolbarBtn("H3", "H3")}
              {toolbarBtn("P", "P")}
              <span style={{ width: 1, height: 20, background: t.border, margin: "0 4px" }} />
              {toolbarBtn("B", "B")}
              {toolbarBtn("I", "I")}
              <span style={{ width: 1, height: 20, background: t.border, margin: "0 4px" }} />
              {toolbarBtn("UL", "UL")}
              {toolbarBtn("OL", "OL")}
              <span style={{ width: 1, height: 20, background: t.border, margin: "0 4px" }} />
              <select
                onChange={(e) => { const v = e.target.value; if (v) applyFontSize(v); e.target.value = ""; }}
                defaultValue=""
                title="Розмір шрифту виділеного тексту"
                style={{ ...btnSm, fontFamily: "inherit", cursor: "pointer", padding: "0 6px", minWidth: 78, height: 28 }}
              >
                <option value="" disabled>А⇕ Розмір</option>
                <option value="12">12 px</option>
                <option value="13">13 px</option>
                <option value="14">14 px</option>
                <option value="15">15 px</option>
                <option value="16">16 px</option>
                <option value="18">18 px</option>
                <option value="20">20 px</option>
                <option value="22">22 px</option>
                <option value="24">24 px</option>
                <option value="28">28 px</option>
                <option value="32">32 px</option>
              </select>
              <select
                onChange={(e) => { const v = e.target.value; if (v) applyLineHeight(v); e.target.value = ""; }}
                defaultValue=""
                title="Міжрядковий інтервал виділених абзаців"
                style={{ ...btnSm, fontFamily: "inherit", cursor: "pointer", padding: "0 6px", minWidth: 84, height: 28 }}
              >
                <option value="" disabled>↕ Інтервал</option>
                <option value="1">1.0</option>
                <option value="1.15">1.15</option>
                <option value="1.3">1.3</option>
                <option value="1.5">1.5</option>
                <option value="1.6">1.6</option>
                <option value="1.8">1.8</option>
                <option value="2">2.0</option>
                <option value="2.5">2.5</option>
              </select>
              <select
                onChange={(e) => { const v = e.target.value; if (v !== "") applyBlockSpacing(v); e.target.value = ""; }}
                defaultValue=""
                title="Відступ між абзацами (виділення)"
                style={{ ...btnSm, fontFamily: "inherit", cursor: "pointer", padding: "0 6px", minWidth: 84, height: 28 }}
              >
                <option value="" disabled>⇕ Відступ</option>
                <option value="0">0</option>
                <option value="4">4 px</option>
                <option value="8">8 px</option>
                <option value="12">12 px</option>
                <option value="16">16 px</option>
                <option value="24">24 px</option>
              </select>
              <button
                onClick={clearTextFormat}
                title="Скинути розмір, інтервал і відступи у виділенні"
                style={{ ...btnSm, fontFamily: "inherit", fontWeight: 700 }}
              >⨯ Скинути</button>
              <span style={{ width: 1, height: 20, background: t.border, margin: "0 4px" }} />
              <button onClick={aiFixText} disabled={aiFixing || !seoScore} style={{
                ...btnSm, fontSize: 11, fontWeight: 700,
                background: aiFixing ? "rgba(16,185,129,0.1)" : "rgba(16,185,129,0.05)",
                color: "#10b981", borderColor: "#10b98133",
                opacity: aiFixing || !seoScore ? 0.6 : 1,
              }}>
                {aiFixing
                  ? (aiFixIter
                      ? `Коригую ${aiFixIter.current}/${aiFixIter.max} (оцінка ${aiFixIter.score})…`
                      : "Коригую…")
                  : "AI коригувати до 70+"}
              </button>
              <span style={{ width: 1, height: 20, background: t.border, margin: "0 4px" }} />
              <button onClick={() => setShowHtmlImport(true)} style={{ ...btnSm, fontSize: 11, color: "#f59e0b", borderColor: "#f59e0b33" }}>&lt;/&gt; HTML</button>
              <button onClick={() => {
                if (!editorRef.current) return;
                const raw = editorRef.current.innerHTML;
                setHtmlImportText(raw);
                setShowHtmlExport(true);
              }} style={{ ...btnSm, fontSize: 11, color: t.textMuted }}>Код</button>
              <span style={{ width: 1, height: 20, background: t.border, margin: "0 4px" }} />
              <button onClick={wikiLink} disabled={!clusters.length}
                title="Авто-перелінковка тексту по ключах кластерів: словоформи, без посилань на саму сторінку"
                style={{ ...btnSm, fontSize: 11, fontWeight: 700, color: "#6366f1", borderColor: "#6366f133", opacity: clusters.length ? 1 : 0.6 }}>🔗 Вікі</button>
            </div>

            {/* Editable area */}
            <div
              ref={editorRef}
              contentEditable
              onInput={() => { runSeoAnalysis(); autoSave(); }}
              onPaste={handlePaste}
              style={{
                minHeight: 400, padding: "20px 24px", outline: "none",
                fontFamily: "'Georgia', serif", fontSize: 15, lineHeight: 1.7,
                color: t.text, background: t.bgCard,
              }}
            />

            {/* Action buttons */}
            <div style={{ display: "flex", gap: 8, padding: "10px 12px", borderTop: `1px solid ${t.border}`, alignItems: "center" }}>
              <button style={btnP} onClick={handleSave} disabled={saving}>
                {saving ? "Збереження..." : "Зберегти"}
              </button>
              {publishedUrls.has(selectedUrl) ? (
                <>
                  <span style={{ fontSize: 11, color: "#10b981", fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#10b981", display: "inline-block" }} />
                    Опубліковано
                  </span>
                  <button onClick={handleUnpublish} style={{ ...btnSm, color: "#ef4444", borderColor: "#ef444433", fontSize: 11 }}>Зняти</button>
                </>
              ) : (
                <button onClick={handlePublish} disabled={publishing} style={{
                  padding: "6px 16px", borderRadius: 8, border: "none", cursor: publishing ? "default" : "pointer",
                  background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff",
                  fontSize: 12, fontWeight: 700, fontFamily: "inherit", opacity: publishing ? 0.6 : 1,
                }}>
                  {publishing ? "Публікація..." : "Опублікувати на сайт"}
                </button>
              )}
              <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontSize: 11, color: t.textMuted, marginLeft: 8 }}>
                <input type="checkbox" checked={readMore} onChange={e => setReadMore(e.target.checked)} style={{ cursor: "pointer" }} />
                Читати далі
              </label>
              <span style={{ marginLeft: "auto", fontSize: 10, color: t.textMuted }}>через Spalah SDK</span>
            </div>

            {/* ─── Коментарі клієнта (тред) ─── */}
            {currentTextId && (
              <div style={{ borderTop: `1px solid ${t.border}`, padding: "14px 16px" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>
                  💬 Коментарі клієнта{textComments.length ? ` · ${textComments.length}` : ""}
                </div>
                {textComments.length === 0 ? (
                  <div style={{ fontSize: 12, color: t.textMuted, fontStyle: "italic" }}>Поки що коментарів немає.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
                    {textComments.map(c => {
                      const isAdmin = c.author_type === "admin";
                      return (
                        <div key={c.id} style={{
                          alignSelf: isAdmin ? "flex-end" : "flex-start", maxWidth: "85%",
                          background: isAdmin ? "rgba(99,102,241,0.12)" : (dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"),
                          border: `1px solid ${isAdmin ? "rgba(99,102,241,0.25)" : t.border}`,
                          borderRadius: 10, padding: "8px 12px",
                        }}>
                          <div style={{ fontSize: 10, color: isAdmin ? "#6366f1" : t.textMuted, fontWeight: 700, marginBottom: 3 }}>
                            {isAdmin ? `Ви · ${c.author_name || "Менеджер"}` : (c.author_name || "Клієнт")}
                            <span style={{ fontWeight: 400, marginLeft: 6, opacity: 0.7 }}>{new Date(c.created_at).toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                          </div>
                          <div style={{ fontSize: 13, color: t.text, whiteSpace: "pre-wrap", lineHeight: 1.45 }}>{c.body}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <textarea
                    value={replyDraft}
                    onChange={e => setReplyDraft(e.target.value)}
                    placeholder="Відповісти клієнту..."
                    rows={2}
                    style={{ flex: 1, boxSizing: "border-box", background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8, padding: "8px 10px", color: t.text, fontSize: 13, fontFamily: "inherit", resize: "vertical", outline: "none" }}
                  />
                  <button onClick={submitReply} disabled={replyBusy || !replyDraft.trim()} style={{
                    ...btnP, fontSize: 12, alignSelf: "flex-end", opacity: (replyBusy || !replyDraft.trim()) ? 0.5 : 1,
                  }}>{replyBusy ? "..." : "Надіслати"}</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right panel — SEO Analysis */}
        <div style={{ width: 300, flexShrink: 0 }}>
          <div style={{ ...card }}>
            <p style={{ ...stitle, marginTop: 0 }}>SEO Score</p>

            {seoScore ? (
              <>
                {/* Score bar */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: scoreColor(seoScore.total) }}>{seoScore.total}</div>
                  <div style={{ flex: 1, height: 8, borderRadius: 4, background: dark ? "#333" : "#e5e7eb" }}>
                    <div style={{ width: `${seoScore.total}%`, height: "100%", borderRadius: 4, background: scoreColor(seoScore.total), transition: "width .3s" }} />
                  </div>
                </div>

                {/* Checklist */}
                {seoScore.checks.map((c, i) => (
                  <div
                    key={i}
                    onClick={() => setExpandedChecks(prev => ({ ...prev, [i]: !prev[i] }))}
                    style={{ padding: "6px 0", borderBottom: `1px solid ${t.border}`, cursor: "pointer" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                      <span style={{ color: checkColor(c.status), fontWeight: 700, width: 16, textAlign: "center" }}>{checkIcon(c.status)}</span>
                      <span style={{ flex: 1, fontWeight: 500 }}>{c.name}</span>
                      <span style={{ color: t.textMuted, fontSize: 10 }}>{c.weight}б</span>
                    </div>
                    {expandedChecks[i] && (
                      <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4, marginLeft: 22 }}>{c.detail}</div>
                    )}
                  </div>
                ))}
              </>
            ) : (
              <div style={{ fontSize: 12, color: t.textMuted }}>Почніть писати текст для аналізу</div>
            )}

            {/* Uniqueness */}
            <p style={{ ...stitle, marginTop: 16 }}>Унікальність</p>
            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
              <button style={btnSm} onClick={checkInternal} disabled={checkingInternal}>
                {checkingInternal ? "..." : "Внутрішня"}
              </button>
              <button style={btnSm} onClick={checkExternal} disabled={checkingExternal}>
                {checkingExternal ? "..." : "Зовнішня"}
              </button>
            </div>

            {uniquenessResult?.internal && (
              <div style={{ fontSize: 12, marginBottom: 8 }}>
                <div style={{ fontWeight: 600, color: uniquenessResult.internal.uniqueness >= 80 ? "#16a34a" : "#ef4444" }}>
                  Внутрішня: {uniquenessResult.internal.uniqueness}%
                </div>
                {uniquenessResult.internal.matches?.length > 0 && (
                  <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>
                    {uniquenessResult.internal.matches.map((m, i) => (
                      <div key={i}>{m.url} — {m.similarity}% схожість</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {uniquenessResult?.external && (
              <div style={{ fontSize: 12 }}>
                <div style={{ fontWeight: 600, color: uniquenessResult.external.uniqueness >= 80 ? "#16a34a" : "#ef4444" }}>
                  Зовнішня: {uniquenessResult.external.uniqueness}%
                </div>
                {uniquenessResult.external.fragments?.length > 0 && (
                  <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>
                    {uniquenessResult.external.fragments.map((f, i) => (
                      <div key={i} style={{ marginBottom: 2 }}>
                        {f.found ? "✕" : "✓"} {f.fragment.substring(0, 50)}...
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    {editorialUrl && (
      <div onClick={() => setEditorialUrl(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div onClick={e => e.stopPropagation()} style={{ background: dark ? "#16161f" : "#fff", color: t.text, borderRadius: 12, padding: 22, width: 560, maxWidth: "92vw", maxHeight: "86vh", overflow: "auto", border: `1px solid ${t.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: 20 }}>📝</span>
            <div style={{ fontSize: 16, fontWeight: 700, flex: 1 }}>Редакторська перевірка (Human Editorial Gate)</div>
            <button onClick={() => setEditorialUrl(null)} style={{ ...btnG, fontSize: 12, padding: "4px 10px" }}>Закрити</button>
          </div>
          <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 12, wordBreak: "break-all" }}>{editorialUrl.replace(/^https?:\/\/[^/]+/, "")}</div>
          {!editorialData ? <div style={{ fontSize: 13, color: t.textMuted }}>Завантаження…</div> : (() => {
            const job = editorialData.job || {}; const ed = job.editorial || {}; const checks = editorialData.checks || [];
            const doneN = checks.filter((_, i) => ed["c" + i]).length;
            return (<>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14, fontSize: 11 }}>
                {job.risk && <span style={{ padding: "3px 8px", borderRadius: 5, fontWeight: 700, background: job.risk === "high" ? "rgba(239,68,68,0.14)" : job.risk === "medium" ? "rgba(234,179,8,0.14)" : "rgba(16,185,129,0.14)", color: job.risk === "high" ? "#ef4444" : job.risk === "medium" ? "#eab308" : "#10b981" }}>ризик: {job.risk}</span>}
                {job.quality != null && <span style={{ padding: "3px 8px", borderRadius: 5, background: "rgba(99,102,241,0.12)", color: "#6366f1" }}>якість {job.quality}</span>}
                {job.mechanical != null && <span style={{ padding: "3px 8px", borderRadius: 5, background: "rgba(99,102,241,0.12)", color: "#6366f1" }}>механічних: {job.mechanical}</span>}
              </div>
              {checks.map((c, i) => (
                <label key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 0", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f2f2f5"}`, cursor: "pointer", fontSize: 13 }}>
                  <input type="checkbox" checked={!!ed["c" + i]} onChange={() => toggleEditorialCheck(i)} style={{ marginTop: 2 }} />
                  <span>{c}</span>
                </label>
              ))}
              <div style={{ marginTop: 14, fontSize: 13, fontWeight: 700, color: doneN === checks.length ? "#10b981" : t.text }}>Перевірено {doneN} з {checks.length}{doneN === checks.length ? " ✓" : ""}</div>
            </>);
          })()}
        </div>
      </div>
    )}
    </>
  );
};

// ─── Text status badge ───────────────────────────────────────────────
function StatusBadge({ status, feedback }) {
  if (!status || status === "draft") return null;
  const map = {
    pending_client: { label: "На погодженні", icon: "📤", color: "#6366f1", bg: "rgba(99,102,241,0.12)" },
    approved: { label: "Погоджено", icon: "✅", color: "#10b981", bg: "rgba(16,185,129,0.12)" },
    rejected: { label: "Переписати", icon: "❌", color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
  };
  const s = map[status];
  if (!s) return null;
  const tip = feedback?.comment
    ? `${s.label}\n\nКоментар клієнта:\n${feedback.comment}`
    : s.label;
  return (
    <span title={tip} style={{
      flexShrink: 0, fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 5,
      background: s.bg, color: s.color, whiteSpace: "nowrap",
      display: "inline-flex", alignItems: "center", gap: 3, cursor: feedback?.comment ? "help" : "default",
    }}>
      {s.icon} {s.label}{feedback?.comment ? " 💬" : ""}
    </span>
  );
}

// Статус сторінки в пайплайні копірайтингу (для SEO-шника): у кого й на якому етапі.
function WriterBadge({ writerStatus, writerName }) {
  if (!writerStatus) return null;
  const who = writerName ? `: ${writerName}` : "";
  const map = {
    assigned: { label: writerName ? `Взяв${who}` : "У пулі", icon: "✍", color: "#6366f1", bg: "rgba(99,102,241,0.12)" },
    in_progress: { label: `Пише${who}`, icon: "✍", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
    submitted: { label: "На перевірці редактора", icon: "👁", color: "#0ea5e9", bg: "rgba(14,165,233,0.12)" },
    revisions: { label: `Правки${who}`, icon: "⟲", color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
    accepted: { label: "Готово (редактор прийняв)", icon: "✓", color: "#16a34a", bg: "rgba(22,163,74,0.12)" },
  };
  const s = map[writerStatus];
  if (!s) return null;
  return (
    <span title={`Копірайтинг — ${s.label}`} style={{
      flexShrink: 0, fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 5,
      background: s.bg, color: s.color, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 3,
    }}>
      {s.icon} {s.label}
    </span>
  );
}

// Звідки текст: оригінал з сайту (підтягнутий) чи створений/переписаний у Spalah.
function OriginBadge({ origin }) {
  if (!origin) return null;
  const s = origin === "site"
    ? { label: "оригінал з сайту", icon: "📄", color: "#0ea5e9", bg: "rgba(14,165,233,0.12)" }
    : { label: "переписано в Spalah", icon: "✨", color: "#16a34a", bg: "rgba(22,163,74,0.12)" };
  return (
    <span title={s.label} style={{
      flexShrink: 0, fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 5,
      background: s.bg, color: s.color, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 3,
    }}>{s.icon} {s.label}</span>
  );
}

// ═══ GROWTH POINTS VIEW ═══
